import type { SettingsListRuntime } from "./settingsListTypes";
import { useResolvedSettingsListRuntime } from "./settingsListRuntimeContext";
import "./settingsList.css";
import type { AntdSorterInput } from "./proTableSortPersistence";
import { PlusOutlined } from "@ant-design/icons";
import type { ListActionRef, ListColumn } from "../list/listColumn";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";
import { backgroundQueryRefresh } from "../system/backgroundQueryRefresh";
import { refreshQueriesAfterWrite } from "../operations/refreshQueriesAfterWrite";
import { Alert,App,Button,Card,Flex,Grid,theme } from "../ui";
import React from "react";

import { cardDefaults, layoutDefaults, spacing } from "./settingsListDesign";
import { createFontMeasureContext,measureTextWidth } from "../utils/fontMeasureContext";
import type { LocalizedText } from "./settingsListTypes";

import { contextScopeWidenMode } from "./contextScopeWiden";
import { useOptionalAppTheme } from "../theme/useAppTheme";

import { deriveColumnSorter,useProTableSortPersistence,type SortableColumnExtension } from "./proTableSortPersistence";
import {
ACTION_BUTTON_ICON_ONLY_WIDTH_PX,
ACTION_ICON_ONLY_MIN_WIDTH,
collectSettingsListActionButtonLabels,
countSettingsListActionNodes,
estimateSettingsListActionButtonWidth,
estimateSettingsListActionsWidth,
estimateSettingsListIconActionsWidth,
getButtonLabel,
LEGACY_ACTION_SLOT_FALLBACK_COUNT,
normalizeSettingsListActionItems,
SETTINGS_LIST_MAX_ROW_ACTIONS,

renderSettingsListStyleActionCell,
withIconOnlyActions,
type SettingsListActionCellLayout,
} from "./settingsListActionColumn";
import {
collectSettingsListRows,

getSettingsListNestedRows,
getSettingsListValueByPath,
safeSettingsListCellNode,
settingsListDataIndexPath,
} from "./settingsListDataAccess";
import { useProTableColumnEditor,type ColumnDefinition } from "./useProTableColumnEditor";
import { useSettingsListTableOverflowMeasure } from "./useSettingsListTableOverflowMeasure";
import { SettingsListFooter, SettingsListRows, type SettingsListRowsActionProbe, type SettingsListRowsFit, type SettingsListRowsProps } from "./SettingsListRows";
import { isSettingsListGroupElement } from "./settingsListElementRoles";
import { isListActionVisible,listActionsToSlots,type ListActionDefinition } from "./listActions";
import { ACTIONS_COLUMN_KEY, ACTIONS_COLUMN_KEY_PREFIX, EDIT_ACTION_KEY, GENERIC_ERROR_MESSAGES, SETTINGS_COLUMN_KEY, VALUE_TYPE_OPTION } from "./settingsListConstants";

/** Erweiterung für exakte Spaltenbreiten-Berechnung: Anzeige-Text pro Zeile (z. B. formatiertes Datum, "1.5 MB"). */
export type ProColumnsWithMeasure<T = object> = ListColumn<T> & SortableColumnExtension<T> & {
  measureDisplayValue?: (record: T) => string;
  /** Zuschlag in px für umrandete/farbige Darstellung (z. B. Tag/Badge), damit die Spalte nicht zu schmal wird. */
  measureExtraPx?: number;
  /**
   * Rolle in der Kartenansicht der Darstellung "rows": "primary" ist der Kartentitel (Standard: erste
   * Datenspalte), "media" steht als Bild neben dem Titel statt als beschriftetes Feld.
   */
  listRole?: "primary" | "media";
};

const { useBreakpoint } = Grid;
const standaloneQueryClient = new QueryClient({ defaultOptions: { queries: {
  refetchOnWindowFocus: backgroundQueryRefresh,
  refetchOnReconnect: backgroundQueryRefresh,
} } });
const DEFAULT_SETTINGS_LIST_PAGE_SIZE = 20;
/** Unter dieser Breite wird das gestauchte Raster der Darstellung "rows" zu Karten. */
const SETTINGS_LIST_SELECTION_COLUMN_WIDTH = 32;
const SETTINGS_LIST_TREE_EXPAND_COLUMN_WIDTH = 48;

function settingsListPageSizeStorageKey(accountId: string | null | undefined, tableKey: string): string {
  return `settingsList.pageSize.${(accountId || "anon").trim() || "anon"}.${tableKey}`;
}

function loadSettingsListPageSize(
  accountId: string | null | undefined,
  tableKey: string,
  fallback: number,
): number {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = Number(window.localStorage.getItem(settingsListPageSizeStorageKey(accountId, tableKey)));
    return Number.isFinite(stored) && stored > 0 ? stored : fallback;
  } catch {
    return fallback;
  }
}

function saveSettingsListPageSize(
  accountId: string | null | undefined,
  tableKey: string,
  pageSize: number,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      settingsListPageSizeStorageKey(accountId, tableKey),
      String(pageSize),
    );
  } catch {
    // Browser storage may be unavailable; the list remains usable.
  }
}

const WRAP_CELL_STYLE: React.CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
};
// Pixel-Schätzung der Header-Breite: muss mindestens die real gerenderte Breite erreichen, sonst werden
// Header-Texte in der Fallback-Spaltenbreite (= headerHint, wenn Inhalt nicht passt) abgeschnitten.
// Mit 7.5px/char + 22+18=40 Padding war die Schätzung systematisch 5–14px ZU KLEIN, mit 8.5/24 (42)
// dagegen ~5–10px ZU GROSS (Folge: Karten-Modus zu früh). Empirisch kalibriert auf 8.3/24 (=42 inkl.
// Sorter): Schätzung 1–3 px über realem Render – garantiert kein Header-Abschneiden, ohne zu früh in
// Karten zu kippen.
const COLUMN_HEADER_CHAR_WIDTH_PX = 8.3;
const COLUMN_HEADER_HORIZONTAL_PADDING_PX = 24;
const COLUMN_HEADER_SORTER_WIDTH_PX = 18;
const COLUMN_HEADER_MIN_WIDTH_PX = 72;
const COLUMN_CELL_CHAR_WIDTH_PX = 6.8;
const COLUMN_CELL_HORIZONTAL_PADDING_PX = 16;
const COLUMN_MEASURED_WIDTH_CAP_PX = 360;
const COLUMN_MEASURED_ROWS_LIMIT = 30;
// Modus-Sicherheitspuffer. Früher 24px – das verschob die Schwelle so weit nach oben, dass Tabellen
// vorzeitig in Kartenansicht kippten, obwohl optisch noch Platz war. Da die Header-Schätzung jetzt sehr
// nah am realen Render liegt, genügt 0px – die Hysterese zwischen den Modi wird ohnehin separat über
// OVERFLOW_HYSTERESIS_PX in nextModeForWidth geregelt.
const TABLE_MODE_SWITCH_GUARD_PX = 0;
// Sicherheitspuffer für den Vollmodus (Icon+Text). Früher 96px – das war für ein anderes Modell
// gedacht (Spaltenbreiten kannten den Inhalt nicht). Da die Inhaltsbreite jetzt in die Schwelle einfließt
// (über `columnWidthHints` inkl. Auto-Messung), genügt 0 → Vollmodus wird gewählt, sobald Inhalt + Buttons
// + GUARD passen. Verhindert vorzeitigen Icon-Modus, ohne Überlauf zu riskieren (die Schwelle deckt das
// volle Inhalts-Sum bereits ab).
const TABLE_FULL_ACTION_MODE_SAFETY_PX = 0;

function requestErrorMessage(error: unknown, fallback: string): string {
  
  if (error instanceof Error && error.message && !GENERIC_ERROR_MESSAGES.includes(error.message)) return error.message;
  return fallback;
}

// Pixelgenaue Header-Text-Messung mit der echten Font (genau wie der Browser ihn rendert) – damit ist
// die Schwelle nicht mehr von einer Schätz-Reserve aufgebläht, der Modus-Wechsel passiert erst dann,
// wenn der Header tatsächlich nicht mehr passt.
const getHeaderMeasureContext = createFontMeasureContext(".slr-head-cell", "500");

function estimateHeaderMinWidth(title: React.ReactNode): number {
  let text: string | null = null;
  if (typeof title === "string") text = title;
  else if (typeof title === "number") text = String(title);
  if (text == null) return COLUMN_HEADER_MIN_WIDTH_PX;

  const textWidth = measureTextWidth(getHeaderMeasureContext, text, COLUMN_HEADER_CHAR_WIDTH_PX);
  return Math.max(
    COLUMN_HEADER_MIN_WIDTH_PX,
    textWidth + COLUMN_HEADER_HORIZONTAL_PADDING_PX + COLUMN_HEADER_SORTER_WIDTH_PX
  );
}

function estimateCellContentWidth(value: string, extraPx = 0): number {
  if (!value) return 0;
  return Math.min(
    COLUMN_MEASURED_WIDTH_CAP_PX,
    Math.ceil(value.length * COLUMN_CELL_CHAR_WIDTH_PX) + COLUMN_CELL_HORIZONTAL_PADDING_PX + extraPx
  );
}

/**
 * Liest den Rohwert einer Zelle anhand des `dataIndex` (String, Zahl oder Pfad-Array) –
 * für die automatische Spaltenbreiten-Messung primitiver Werte (siehe `columnWidthHints`).
 */
function readSettingsListColumnValue(record: object, dataIndex: unknown): unknown {
  if (dataIndex == null) return undefined;
  if (typeof dataIndex !== "string" && typeof dataIndex !== "number" && !Array.isArray(dataIndex)) return undefined;
  // Der Zeilentyp ist an dieser Stelle nicht bekannt (jede Liste bringt einen eigenen mit); gelesen
  // wird ueber den dataIndex der Spalte. Deshalb der Umweg ueber einen Indexzugriff auf unknown.
  const asRecord = record as Record<string, unknown>;
  if (Array.isArray(dataIndex)) {
    let current: unknown = asRecord;
    for (const segment of dataIndex) {
      if (current == null || typeof current !== "object") return undefined;
      current = (current as Record<string, unknown>)[String(segment)];
    }
    return current;
  }
  return asRecord[String(dataIndex)];
}

/**
 * Sammelt den reinen Text-Inhalt eines gerenderten Zell-Nodes (rekursiv über children).
 * Nötig, weil viele Spalten ihren Anzeige-Text per `render` aus mehreren Feldern bauen
 * (z. B. Datums-Bereich „01.06.2026 – 31.12.2026", „12,00 €/Monat") – der Rohwert eines
 * einzelnen `dataIndex` reicht dann zur Breiten-Messung nicht. Icons ohne Text liefern "".
 */
function extractSettingsListCellText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractSettingsListCellText).join("");
  if (React.isValidElement(node)) {
    const el = node as React.ReactElement<{ children?: React.ReactNode }>;
    let acc = "";
    React.Children.forEach(el.props?.children, (child) => {
      acc += extractSettingsListCellText(child);
    });
    return acc;
  }
  return "";
}

/**
 * Zerlegt einen Zell-Node in "atomare" Text-Segmente für die Breitenmessung. Hintergrund: Spalten, die
 * eine umbrechbare Tag-Liste rendern (`<Space wrap>` mit mehreren `<Tag>`, z. B. „Genutzt von"), brauchen
 * NICHT die Summe aller Tag-Texte als Spaltenbreite – die Tags brechen ja um. Nötig ist nur die Breite
 * des breitesten einzelnen Tags. Wird hier ein Space/Flex-Container erkannt, liefert jede direkte Tag-/
 * Kind-Gruppe ein eigenes Segment; sonst ist der gesamte Text EIN Segment (normale Text-Spalten bleiben
 * unverändert). Der Aufrufer nimmt das Maximum der Segment-Breiten.
 */
function collectSettingsListCellSegments(node: React.ReactNode): string[] {
  if (React.isValidElement(node)) {
    const el = node as React.ReactElement<{ children?: React.ReactNode }>;
    if (isSettingsListGroupElement(el)) {
      const segments: string[] = [];
      React.Children.forEach(el.props?.children, (child) => {
        const txt = extractSettingsListCellText(child).trim();
        if (txt) segments.push(txt);
      });
      if (segments.length > 0) return segments;
    }
    // Einzelnes Wrapper-Element (z. B. Typography.Text um einen Space) → rekursiv weiterreichen.
    const kids = React.Children.toArray(el.props?.children);
    if (kids.length === 1 && React.isValidElement(kids[0])) {
      return collectSettingsListCellSegments(kids[0]);
    }
  }
  const whole = extractSettingsListCellText(node).trim();
  return whole ? [whole] : [];
}
/** Breite der Spalte „nur Zahnrad“, wenn es keine Aktions-Buttons gibt. */
const SETTINGS_ONLY_COLUMN_WIDTH = 56;

export type SettingsListRequestParams = {
  current?: number;
  pageSize?: number;
  /** Nur mit scopeWidenHint: dieselben Filter in allen zugänglichen Bereichen, ohne Anzeige-Nebenwirkungen. */
  scope?: "all";
};

export type SettingsListRequestResult<T> = {
  data: T[];
  success: boolean;
  total: number;
  /** A preserved snapshot is displayable, but is not a successful fresh read. */
  requestError?: string;
};
export type SettingsListRequest<T> =
  | (() => Promise<SettingsListRequestResult<T>>)
  | ((params: SettingsListRequestParams, sort?: Record<string, "ascend" | "descend">) => Promise<SettingsListRequestResult<T>>);

export type SettingsListDataMode = "complete-scope" | "server-query";

// `any` hier bewusst: `ProColumns<object>` ist von `ProColumns<KonkreteZeile>` nicht zuweisbar
// (Gegenvarianz in den render-Parametern). Ausprobiert und von tsc abgelehnt — siehe Plan Schritt 5.
type SettingsListExpandableSummaryColumn = ListColumn<any> & {
  /** Schlüssel der logischen Spalte im Spalteneditor, falls Gruppen- und Detailzeile technisch unterschiedliche Keys verwenden. */
  columnEditorKey?: string;
};

type SettingsListGroupRow<T extends object> = {
  key: string;
  detailRows: T[];
} & Record<string, unknown>;

type SettingsListGroupRowSelection<T extends object> = {
  selectedRowKeys?: React.Key[];
  onChange?: (selectedRowKeys: React.Key[], selectedRows: SettingsListGroupRow<T>[]) => void;
  getCheckboxProps?: (record: SettingsListGroupRow<T>) => { disabled?: boolean; title?: string };
  hideSelectAll?: boolean;
  columnWidth?: number;
  selectOnRowClick?: boolean;
};

/** Steuerung der Liste von außen; gleichbedeutend mit `ListActionRef` (shared/frontend/list/listColumn). */
export type SettingsListActionRef = ListActionRef;

export interface SettingsListPageProps<T extends object> {
  /** Ausdrückliche Laufzeit; sonst aus `SettingsListRuntimeProvider` (settingsListRuntimeContext). */
  runtime?: SettingsListRuntime;
  storageKey: string;
  rowKey: string | ((record: T) => string);
  /**
   * Bereits geladene Zeilen statt `request` (Datenmodus „dataSource“): kein Abruf, kein Abfrage-Cache;
   * Sortieren und Blättern lokal, bei gesteuertem Blättern (`pagination.current`/`total`) die Seite des Aufrufers.
   */
  dataSource?: readonly T[];
  /** Ladezustand im Datenmodus „dataSource“. */
  loading?: boolean;
  /**
   * Ladefunktion der Liste. **Ihre Identität ist der Datenschlüssel:** Eine neue Funktion bedeutet
   * „Filter, Bereich oder Kontext haben sich geändert" — die Liste wird vollständig neu geladen und
   * ist währenddessen gesperrt, aufgeklappte Zeileninhalte eingeschlossen. Deshalb per
   * `useCallback` mit genau den Werten stabilisieren, die das Ergebnis verändern; nie inline
   * schreiben (sonst lädt jede Neuzeichnung der Seite die Liste neu).
   *
   * Bewusst kein expliziter `requestKey`: Die Abhängigkeiten eines `useCallback` prüft
   * `react-hooks/exhaustive-deps`, ein frei gepflegter Schlüssel würde vergessene Filter still
   * übergehen. m1member erzwingt die Stabilität mit `check-settings-list-request-stability.mjs`.
   * Entfällt im Datenmodus „dataSource“.
   */
  request?: SettingsListRequest<T>;
  /**
   * complete-scope: Der Request liefert die vollständige fachliche Partition; Sortierung geschieht lokal.
   * server-query: Der Server verantwortet Sortierung und Pagination; die empfangene Seite wird nie lokal umsortiert.
   */
  dataMode?: SettingsListDataMode;
  /** Non-secret identity of the server scope; preferences keep their stable storageKey. */
  requestScopeKey?: string;
  columnDefinitions: ColumnDefinition[];
  allColumnsMap: Record<string, ProColumnsWithMeasure<T>>;
  /**
   * Padding-Variante für die umgebende Card.
   * - compact: 12px (Standard für alle normalen Arbeitslisten)
   * - spacious: 24px (nur für explizit begründete Nicht-Standard-Ausnahmen)
   */
  cardPadding?: "compact" | "spacious";
  /**
   * Optionaler Header oberhalb von Toolbar + Tabelle (z. B. in Personendetail).
   * Wird links angezeigt (typischerweise ein Titel).
   */
  headerTitle?: React.ReactNode;
  /**
   * Optionaler Header-Bereich rechts (z. B. "Hinzufügen"-Button).
   */
  headerExtra?: React.ReactNode;
  /** Optional content within the card, between its header and the list toolbar. */
  beforeTable?: React.ReactNode;
  /** Optional summary/footer within the card after the table or mobile list. */
  afterTable?: React.ReactNode;
  /**
   * Aktions-Spalte (z. B. Bearbeiten/Löschen).
   * Die Komponente setzt immer align: "right" und (wenn fixActionsColumn) fixed: "right" –
   * Consumer müssen align/fixed in actionsColumn nicht angeben.
   * In der Kartenansicht wird render(value, record, index, { cardView: true }) aufgerufen –
   * bei Bedarf Text+Icon rendern, wenn opts?.cardView, sonst kann Icon-Only genutzt werden.
   * Optional: slotCount – maximale Anzahl Aktions-Buttons. Wenn render() weniger Einträge zurückgibt
   * (oder null/undefined für einen Slot), werden fehlende Slots automatisch als unsichtbare
   * Platzhalter gerendert, damit das Layout überall gleich bleibt (kein pro-Liste actionSlots nötig).
   */
  actionsColumn?: ListColumn<T> & { slotCount?: number };
  /**
   * Einfache Alternative: Liste von Slots mit show(record) und render(record).
   * Wenn show false ist, wird der Button nur dann als unsichtbarer Platzhalter gerendert,
   * wenn derselbe Slot in der aktuell geladenen Tabelle mindestens einmal sichtbar ist.
   * Nur nutzbar zusammen mit actionsColumn (für key/title/fixed). Überschreibt actionsColumn.render.
   */
  actionSlots?: Array<{ show: (record: T) => boolean; render: (record: T) => React.ReactNode }>;
  /**
   * Schlanke Ant-Design-Standardisierung fuer Listenaktionen. Neue Standardlisten sollen
   * strukturierte Actions verwenden; actionSlots bleibt fuer Sonderfaelle/Legacy bestehen.
   */
  actions?: ListActionDefinition<T>[];
  toolBarActions?: React.ReactNode[];
  /** Filter/Comboboxen oberhalb der Tabelle (z.B. Select, DatePicker) */
  toolbarExtra?: React.ReactNode;
  /** Buttons rechts, links neben dem Hinzufügen-Button (z.B. „Metadaten aktualisieren“) */
  toolbarRightExtra?: React.ReactNode;
  /** Wenn true: rechter Toolbar-Bereich (toolbarRightExtra + Buttons) ist rechts ausgerichtet (justifyContent: flex-end). */
  toolbarRightAlign?: boolean;
  /**
   * Einfacher Zeilen-Klick (z. B. Detail öffnen). Klicks auf interaktive Elemente (Buttons, Links, Inputs) werden ignoriert.
   * Einen Doppelklick gibt es nicht. Ohne diesen Klick übernimmt ihn die Aktion `edit` aus `actions`; ihr Knopf entfällt dann.
   */
  onRowClick?: (record: T) => void;
  onChange?: (
    pagination: unknown,
    filters: unknown,
    sorter: AntdSorterInput | AntdSorterInput[] | null | undefined,
    extra: unknown
  ) => void;
  onAdd?: () => void;
  addButtonText?: LocalizedText;
  addButtonDisabled?: boolean;
  actionRef?: React.RefObject<ListActionRef | SettingsListActionRef | null>;
  /**
   * Optionale Pagination (z. B. pageSize für Logs). `false` = alle Zeilen auf einer Seite.
   * Gesteuertes Blättern im Datenmodus „dataSource“: `current` + `total` gesetzt, `dataSource` ist die
   * aktuelle Seite, Wechsel meldet `onChange`.
   */
  pagination?: false | {
    pageSize?: number;
    /** Wählbare Seitengrößen (Standard 10/20/50/100); `pageSize` ist der Startwert, solange der Benutzer nichts gewählt hat. */
    pageSizeOptions?: readonly number[];
    showTotal?: (total: number, range: [number, number]) => React.ReactNode;
    itemRender?: (
      page: number,
      type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
      originalElement: React.ReactElement
    ) => React.ReactNode;
    current?: number;
    total?: number;
    onChange?: (page: number, pageSize: number) => void;
  };
  /**
   * Gesteuertes Blättern: Die Liste sortiert die übergebene Seite nicht selbst, sondern meldet die aktive
   * Sortierung (beim Einhängen und bei jeder Änderung) an den Aufrufer, der sie an den Server gibt.
   * `undefined` = keine Sortierung. Ohne diesen Rückkanal sortiert gesteuertes Blättern nur die Seite.
   */
  onRequestSortChange?: (sort: Record<string, "ascend" | "descend"> | undefined) => void;
  /** Summenzeile unter den Zeilen: Wert je Spaltenschlüssel, berechnet über alle Zeilen in Anzeigereihenfolge. */
  summary?: (data: readonly T[]) => Partial<Record<string, React.ReactNode>>;
  /** Optionaler Inhalt links in der Pagination-Zeile (z. B. Summen der aktuellen Liste/Auswahl). */
  paginationSummary?: (total: number, range: [number, number]) => React.ReactNode;
  /** Fachlicher Abschnittstitel. Ein Seitentitel gehört ausschließlich zum PageFrame. */
  sectionTitle?: React.ReactNode;
  /** Optionaler Inhalt im Listenkopf oberhalb der Toolbar, z. B. Status- oder Hilfetext. */
  titleExtra?: React.ReactNode;
  /** Optional: eigener Storage-Key für Spalteneditor (sonst `settings.${storageKey}-columns`). */
  columnEditorStorageKey?: string;
  /** Optionale Klasse für die ProTable-Instanz, wenn Fachlisten eigene Tabellen-Styles benötigen. */
  tableClassName?: string;
  /**
   * Wenn false: Zahnrad (Spalten anpassen) nur in der Tabellenkopfzeile ausblenden.
   * Das Zahnrad wird nie in der Toolbar über der Karte angezeigt, sondern nur in der Liste (Tabellenkopf). Standard true.
   */
  showColumnEditor?: boolean;
  /** Optional: Inhalt links neben dem Zahnrad in der Tabellenkopfzeile (z. B. Action-Menü mit „Als CSV exportieren“); in der Kartenansicht mit dem Zahnrad in der Werkzeugleiste. */
  listHeaderExtraLeft?: React.ReactNode;
  /** Wenn keine Aktions-Buttons: Breite der Zahnrad-Spalte (Standard 56). Z. B. 36 für eingebettete Listen. */
  settingsOnlyColumnWidth?: number;
  /** Wenn true: Aktionsspalte fixiert. Standard false, damit Aktionen bei knapper Breite nicht über Datenzellen liegen. */
  fixActionsColumn?: boolean;
  /**
   * Darstellung der Aktions-Spalte in der Tabellenansicht.
   * - horizontal (Standard): eine Zeile wie bisher.
   * - vertical: Aktionen untereinander, rechts; gemeinsames CSS unter `.settings-list-table-wrap` (Klasse `settings-list-actions-vertical-cell`).
   *   Nutzen z. B. Personen-Vertragspaket + Unterverträge, wenn dieselbe Slot-/Platzhalter-Logik wie horizontal gelten soll.
   */
  actionsCellLayout?: SettingsListActionCellLayout;
  /**
   * Optionale Gruppierung: eine Zeile pro Gruppe mit + zum Aufklappen; aufgeklappt: Detail-Tabelle ohne eigene Kopfzeile.
   */
  expandableGroup?: {
    /** Eine Zeile pro Gruppe; jede Zeile muss key und detailRows haben. Achtung: Nicht "children" verwenden – antd Table rendert sonst die Einträge als eigene Zeilen. */
    data: SettingsListGroupRow<T>[];
    summaryColumns: SettingsListExpandableSummaryColumn[];
    childColumnKeys: string[];
    /** A distinct detail schema when allocations differ from their summary row. */
    childColumns?: ProColumnsWithMeasure<T>[];
    /** Limit detail columns per group, e.g. omit uniform values shown in its summary. */
    getChildColumnKeys?: (group: SettingsListGroupRow<T>, visibleSummaryKeys: string[]) => string[];
    loading?: boolean;
    /** Auswahl ganzer Gruppen; der Consumer entscheidet, wie die enthaltenen Detailzeilen fachlich verarbeitet werden. */
    rowSelection?: SettingsListGroupRowSelection<T>;
    childExpandedRowRender?: (record: T) => React.ReactNode | null;
    childExpandedRowKeys?: React.Key[];
    childOnExpandedRowsChange?: (keys: readonly React.Key[]) => void;
  };
  /** Optionaler Inlay-Inhalt unter einer normalen Tabellenzeile, z. B. Dokument-/Berichtsvorschau. */
  expandedRowRender?: (record: T) => React.ReactNode | null;
  expandedRowKeys?: React.Key[];
  /** @deprecated Wirkungslos seit der Zeilenliste (kein Aufklappschalter); Aufklappen steuert `expandedRowKeys`. */
  onExpandedRowsChange?: (expandedKeys: readonly React.Key[]) => void;
  /** Aufklappen per Schalter in der Zeile (ungesteuert); `rowExpandable` begrenzt auf einzelne Zeilen. */
  expandable?: { render: (record: T) => React.ReactNode; rowExpandable?: (record: T) => boolean };
  /** Optionale Zeilenauswahl (Checkboxen), z. B. für „Zahlung erfassen“ oder „Ratenzahlung“ mit Übernahme in Drawer. */
  rowSelection?: {
    selectedRowKeys?: React.Key[];
    onChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
    getCheckboxProps?: (record: T) => { disabled?: boolean; title?: string };
    /** Wie im Tab Beiträge: keine Checkboxen, Zeilenklick togglet Auswahl. */
    hideSelectAll?: boolean;
    columnWidth?: number;
    renderCell?: () => null;
    alwaysShowAlert?: boolean;
  };
  /** Bei Zeilenauswahl: Klick auf Zeile togglet Auswahl (wie Tab Beiträge). Muss mit rowSelection verwendet werden. Event für Ignorieren von Klicks auf Buttons/Links. */
  onRowClickForSelection?: (record: T, e: React.MouseEvent) => void;
  /** Optionale CSS-Klasse pro Zeile (z. B. für Hervorhebung). */
  rowClassName?: (record: T) => string;
  /** Manuelle Zeilenreihenfolge per Ziehgriff (`SettingsListRows.rowReorder`); Spaltensortierung entfällt dann. */
  rowReorder?: SettingsListRowsProps<T>["rowReorder"];
  rowDrag?: SettingsListRowsProps<T>["rowDrag"];
  /** Meldet die sichtbaren Spaltenschlüssel in Anzeigereihenfolge (z. B. für einen Export derselben Spalten). */
  onVisibleColumnsChange?: (keys: string[]) => void;
  /** Optional: aktuell hervorgehobene Zeile, z. B. für Tastatur-Navigation aus externen Suchfeldern. */
  activeRowKey?: React.Key | null;
  /** Optional: Klick auf eine Zeile macht sie zur aktiven Zeile. */
  onActiveRowKeyChange?: (key: React.Key | null, record: T | null) => void;
  /** Optional: informiert Consumer über aktuell geladene, sortierte Daten. */
  onVisibleRowsChange?: (rows: T[]) => void;
  /**
   * Optional: rendert für eine Zeile statt der Originalzellen einen über mehrere Spalten gespannten
   * Inhalt (z. B. Live-Fortschritt eines Hintergrundjobs). Nur Daten-Spalten werden überspannt;
   * die Aktions-Spalte (fixed: right) bleibt strukturell unverändert, kann aber per
   * `actionSlots`/`actionsColumn.render` zeilenabhängig anderen Inhalt liefern.
   */
  rowOverlay?: (record: T) => RowOverlaySpec | null;
  /**
   * Generischer Zeilenzustand fuer Busy-/Locked-Listen. Fachseiten liefern nur den Zustand,
   * SettingsListPage uebernimmt Overlay und Action-Ersetzung zentral.
   */
  rowState?: (record: T) => SettingsListRowState<T> | null;
  /**
   * Wenn true: Kartenansicht für schmale Viewports / Überlauf wird komplett deaktiviert.
   * Sinnvoll für Listen, deren Datenstruktur in Kartenform unleserlich wäre.
   */
  disableCardView?: boolean;
  /**
   * Wenn true: Kartenansicht nur für schmale Breakpoints verwenden, nicht als Desktop-Fallback
   * bei breiten Tabellen. So bleiben Desktop-Flows als Tabelle erhalten.
   */
  preserveDesktopTable?: boolean;
  /**
   * Ant-Design-Breakpoint, ab dem (darunter) auf Kartenansicht gewechselt wird. Default `"md"`
   * (< 768px → Karten). Listen mit vielen Spalten oder breiten Inhalten setzen das auf einen
   * kleineren Breakpoint, damit Karten erst bei wirklich schmalem Viewport greifen — z. B.
   * `"sm"` (< 576px) für eine Mobile-only Karten-Anzeige.
   */
  cardViewBreakpoint?: "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  /**
   * Liste ohne eigene Karte, z. B. in einem Wizard-Schritt, der den Rahmen schon liefert.
   * Zahnrad, Sortierung und Spalteneditor bleiben; Titelkarte und Außenrahmen entfallen.
   */
  embedded?: boolean;
  /** Optional: überschreibt die Standard-Empty-Anzeige bei leerer Tabelle (z. B. " " für nichts). */
  emptyText?: React.ReactNode;
  /**
   * Wenn die Trefferliste leer oder kurz ist und im Header nicht schon alle zugreifbaren Bereiche
   * gewählt sind, im Listenergebnis (leer: Empty-Text, kurz: unter den Zeilen) auf alle Bereiche
   * umschalten, sofern eine Vergleichsanfrage zusätzliche Treffer bestätigt. request unterstützt
   * dafür params.scope="all" ohne Änderungen an Anzeigezustand/Entwürfen; bei dataSource wird
   * scopeWidenRequest verwendet. Ohne bestätigte Mehrtreffer bleibt der Hinweis ausgeblendet.
   */
  scopeWidenHint?: boolean;
  /** Anzahl bei gleicher Filterung in allen zugänglichen Bereichen; für Listen mit dataSource. */
  scopeWidenRequest?: () => Promise<number>;
}

/**
 * Beschreibt einen Zeilen-Overlay: legt fest, welche aufeinanderfolgenden Daten-Spalten zu einer
 * einzigen Zelle zusammengezogen werden und welcher Inhalt dort gerendert wird. Bei festen Keys
 * richtet sich die Reihenfolge nach `columnDefinitions`/sichtbarer Spaltenfolge —
 * `spanFromColumnKey` muss vor `spanToColumnKey` (oder gleich) liegen.
 */
export interface RowOverlaySpec {
  /** Erste zu kollabierende Daten-Spalte (Key aus `columnDefinitions`). */
  spanFromColumnKey?: string;
  /** Letzte zu kollabierende Daten-Spalte (Key aus `columnDefinitions`). */
  spanToColumnKey?: string;
  /**
   * Wenn gesetzt, wird der Overlay-Bereich aus den aktuell sichtbaren Datenspalten berechnet.
   * Das ist robuster bei ausgeblendeten Spalten als feste Start-/Endkeys.
   */
  spanVisibleDataColumns?: boolean;
  /** Anzahl sichtbarer Datenspalten am linken Rand, die frei bleiben sollen. */
  reserveFirstVisibleColumnCount?: number;
  /** Anzahl sichtbarer Datenspalten am rechten Rand, die frei bleiben sollen. */
  reserveLastVisibleColumnCount?: number;
  /** Sichtbare Datenspalten, die nicht vom Overlay belegt werden duerfen. */
  excludedColumnKeys?: readonly string[];
  /** Beliebiger Inhalt, der in der zusammengefassten Zelle gerendert wird. */
  content: React.ReactNode;
}

export interface SettingsListRowState<T extends object> {
  /**
   * Optionaler Ersatz fuer die normalen Datenzellen einer Zeile, z. B. ein Fortschrittsbalken
   * fuer laufende Hintergrundjobs. Die Fachseite entscheidet nur, ob ein Zustand aktiv ist.
   */
  overlay?: RowOverlaySpec | null;
  /** Ersetzt die Standardaktionen der Zeile, z. B. durch "Abbrechen" oder einen Aktiv-Status. */
  replacementActions?: ListActionDefinition<T>[];
  /** Deaktiviert alle oder einzelne Standardaktionen ohne lokales Button-Rendering. */
  disabledActions?: boolean | React.Key[];
}

function SettingsListOuterFrame({
  embedded,
  cardTitle,
  cardExtra,
  cardBodyStyle,
  children,
}: {
  embedded: boolean;
  cardTitle?: React.ReactNode;
  cardExtra?: React.ReactNode;
  cardBodyStyle: React.CSSProperties;
  children: React.ReactNode;
}) {
  if (embedded) return children;
  return (
    <Card
      title={cardTitle}
      extra={cardExtra}
      style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
      styles={{ body: { ...cardBodyStyle, display: "flex", flexDirection: "column", minHeight: 0 } }}
    >
      {children}
    </Card>
  );
}

export function SettingsListCardHeader({ headerTitle, headerExtra, inset = false }: { headerTitle?: React.ReactNode; headerExtra?: React.ReactNode; inset?: boolean }) {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  if (headerTitle == null && headerExtra == null) return null;
  return (
    <Flex
      // `inset`: freistehende Liste ohne Kartenpolster — der Kopf bringt den Innenabstand der Werkzeugleiste mit.
      className={inset ? "settings-list-card-header settings-list-card-header--inset" : "settings-list-card-header"}
      justify="space-between"
      align={isMobile ? "stretch" : "center"}
      vertical={isMobile}
      wrap={isMobile ? false : "wrap"}
      gap={12}
      style={{ marginBottom: inset ? 0 : cardDefaults.listCardHeaderMarginBottom, width: "100%" }}
    >
      {headerTitle != null ? <div style={{ minWidth: 0 }}>{headerTitle}</div> : null}
      {headerExtra != null ? (
        <Flex
          className="settings-list-card-header-extra"
          justify="flex-end"
          align="center"
          wrap="wrap"
          style={{ marginLeft: isMobile ? 0 : "auto", minWidth: 0, width: isMobile ? "100%" : undefined }}
        >
          {headerExtra}
        </Flex>
      ) : null}
    </Flex>
  );
}

function SettingsListToolbarRow({
  toolbarExtra,
  toolbarRightExtra,
  toolbarButtons,
  toolbarRightAlign,
  marginBottom,
}: {
  toolbarExtra?: React.ReactNode;
  toolbarRightExtra?: React.ReactNode;
  toolbarButtons: React.ReactNode[];
  toolbarRightAlign: boolean;
  marginBottom: number;
}) {
  const screens = Grid.useBreakpoint();
  const isStackedToolbar = screens.sm === false;
  if (toolbarExtra == null && toolbarRightExtra == null && toolbarButtons.length === 0) return null;
  const hasRightContent = toolbarRightExtra != null || toolbarButtons.length > 0;
  const hasLeftContent = toolbarExtra != null;
  const leftContent = toolbarExtra != null ? (
    <div
      className="settings-list-toolbar-extra"
      style={{
        flex: "1 1 auto",
        justifyContent: "flex-start",
        minWidth: 0,
        width: isStackedToolbar ? "100%" : undefined
      }}
    >
      {isStackedToolbar ? (
        toolbarExtra
      ) : (
        <div style={{ width: "100%", minWidth: 0 }}>
          {toolbarExtra}
        </div>
      )}
    </div>
  ) : null;
  const rightContent = hasRightContent ? (
    <div
      className="settings-list-toolbar-right"
      style={{
        flex: "0 1 auto",
        marginLeft: !isStackedToolbar && (hasLeftContent || toolbarRightAlign) ? "auto" : undefined,
        minWidth: 0,
        width: isStackedToolbar ? "100%" : undefined,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: spacing.sm,
        justifyContent: "flex-end"
      }}
    >
      {toolbarRightExtra}
      {toolbarButtons}
    </div>
  ) : null;

  return (
    <Flex
      className="settings-list-toolbar-row"
      vertical={isStackedToolbar}
      wrap={isStackedToolbar ? false : "wrap"}
      justify={isStackedToolbar ? "flex-start" : hasLeftContent && hasRightContent ? "space-between" : hasRightContent ? "flex-end" : "flex-start"}
      align={isStackedToolbar ? "stretch" : "flex-end"}
      gap={isStackedToolbar ? 10 : 16}
      style={{ width: "100%", minWidth: 0, marginBottom }}
    >
      {isStackedToolbar ? rightContent : leftContent}
      {isStackedToolbar ? leftContent : rightContent}
    </Flex>
  );
}

export function SettingsListPage<T extends object>({
  runtime: runtimeProp,
  storageKey,
  rowKey,
  dataSource,
  loading: dataSourceLoading = false,
  summary,
  expandable: expandableProp,
  request,
  dataMode = "complete-scope",
  requestScopeKey,
  columnDefinitions,
  allColumnsMap,
  cardPadding = "compact",
  headerTitle,
  headerExtra,
  beforeTable,
  afterTable,
  actionsColumn,
  actionSlots: actionSlotsProp,
  actions,
  toolBarActions = [],
  toolbarExtra,
  toolbarRightExtra,
  toolbarRightAlign = false,
  onRowClick,
  onChange,
  onAdd,
  addButtonText = "Add",
  addButtonDisabled = false,
  actionRef: externalActionRef,
  pagination: requestedPagination,
  onRequestSortChange,
  paginationSummary,
  sectionTitle,
  titleExtra,
  columnEditorStorageKey: columnEditorStorageKeyProp,
  tableClassName,
  showColumnEditor: showColumnEditorProp = true,
  listHeaderExtraLeft,
  settingsOnlyColumnWidth: settingsOnlyColumnWidthProp,
  fixActionsColumn = false,
  actionsCellLayout = "horizontal",
  expandableGroup,
  expandedRowRender,
  expandedRowKeys,
  rowSelection: rowSelectionProp,
  onRowClickForSelection,
  rowClassName: rowClassNameProp,
  rowReorder,
  rowDrag,
  onVisibleColumnsChange,
  activeRowKey,
  onActiveRowKeyChange,
  onVisibleRowsChange,
  rowOverlay,
  rowState,
  disableCardView = false,
  preserveDesktopTable = false,
  cardViewBreakpoint = "md",
  emptyText,
  embedded = false,
  scopeWidenHint = false,
  scopeWidenRequest,
}: SettingsListPageProps<T>) {
  
  
  const { modal } = App.useApp();
  const runtime = useResolvedSettingsListRuntime(runtimeProp);
  const paginationProp = runtime.pagination === false ? false : requestedPagination;
  const { text } = runtime;
  const dataSourceMode = dataSource !== undefined;
  const paginationOptions = paginationProp === false ? undefined : paginationProp;
  const controlledPaging = dataSourceMode && paginationOptions?.current != null && paginationOptions.total != null;
  // Gesteuertes Blättern mit Rückkanal: der Server sortiert, die Seite wird nie lokal umsortiert.
  const callerSortsOnServer = controlledPaging && onRequestSortChange != null;
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const isBreakpointNarrow = !screens[cardViewBreakpoint];
  const [requestError, setRequestError] = React.useState<string | null>(null);
  const resolveRowActions = React.useCallback(
    (record: T, baseActions: ListActionDefinition<T>[]) => {
      const state = rowState?.(record);
      const replacementActions = state?.replacementActions;
      const disabledActions = state?.disabledActions;
      const resolved = replacementActions ?? baseActions;
      if (disabledActions == null || disabledActions === false) return resolved;
      return resolved.map((action) => {
        const disabledByState =
          disabledActions === true ||
          (Array.isArray(disabledActions) && disabledActions.some((key) => key === action.key));
        if (!disabledByState) return action;
        return {
          ...action,
          disabled: true,
        };
      });
    },
    [rowState]
  );

  // Ein Klick auf die Zeile öffnet sie (Ronny, 18.10.2026): ohne eigenen Zeilenklick übernimmt ihn
  // die Bearbeiten-Aktion, deren Knopf daneben doppelt wäre und entfällt. Auswahl- und Gruppenlisten
  // behalten ihr Klickverhalten. Führt ein eigener Zeilenklick dieselbe Funktion aus, entfällt der Knopf ebenso.
  const rowEditAction = !onRowClickForSelection && !expandableGroup
    ? actions?.find((action) => (action.rowClick ?? action.key === EDIT_ACTION_KEY) && action.onClick && !action.render && !action.confirm
      && (!onRowClick || (action.onClick as unknown) === onRowClick))
    : undefined;
  const listActions = React.useMemo(
    () => (rowEditAction ? actions?.filter((action) => action !== rowEditAction) : actions),
    [actions, rowEditAction]
  );
  const effectiveOnRowClick = React.useMemo<((record: T) => void) | undefined>(() => {
    if (onRowClick || !rowEditAction) return onRowClick;
    return (record: T) => {
      const action = resolveRowActions(record, [rowEditAction])[0];
      const usable = action && isListActionVisible(action, record)
        && !(typeof action.disabled === "function" ? action.disabled(record) : action.disabled);
      if (!usable) { rowEditAction.onView?.(record); return; }
      void action.onClick?.(record, undefined as unknown as React.MouseEvent<HTMLElement>);
    };
  }, [onRowClick, rowEditAction, resolveRowActions]);

  const actionSlots = React.useMemo(
    () => (actions?.length ? listActionsToSlots(listActions ?? [], text, modal, resolveRowActions) : actionSlotsProp),
    [actions, listActions, actionSlotsProp, text, modal, resolveRowActions]
  );

  const effectiveRowOverlay = React.useCallback(
    (record: T) => rowState?.(record)?.overlay ?? rowOverlay?.(record) ?? null,
    [rowOverlay, rowState]
  );

  const internalActionRef = React.useRef<ListActionRef>(null);
  /** Bei externer actionRef: ProTable schreibt hier; Reload der Kartenliste (ohne ProTable) über Delegation. */
  const proTableRefForDelegate = React.useRef<ListActionRef>(null);
  const tableActionRef = externalActionRef ? proTableRefForDelegate : internalActionRef;
  const initialPageSize = paginationOptions?.pageSize ?? DEFAULT_SETTINGS_LIST_PAGE_SIZE;
  const [pageSize, setPageSize] = React.useState(() =>
    loadSettingsListPageSize(runtime.accountId, storageKey, initialPageSize));

  React.useEffect(() => {
    setPageSize(loadSettingsListPageSize(runtime.accountId, storageKey, initialPageSize));
  }, [runtime.accountId, initialPageSize, storageKey]);

  // Synchron während des Renders aktualisieren, damit ein direkt nach setState ausgelöstes
  // reload() (z. B. via setTimeout(0)) garantiert die neue Closure sieht — sonst rennt der
  // Reload an einem useEffect vorbei und liest noch die alte request-Funktion (z. B. mit altem
  // "Historische Verträge"/"Historische Beiträge"-Filter).
  const requestRef = React.useRef(request);
  requestRef.current = request;
  // Zaehlt hoch, sobald der Aufrufer eine neue `request`-Closure liefert (Filter-, Bereichs- oder
  // Zustandswechsel der Seite). Als Zustand statt als Ref, damit der Query-Schluessel ihn lesen
  // darf, ohne im Render eine Ref anzufassen — das Nachziehen von Zustand waehrend des Renders ist
  // der von React vorgesehene Weg dafuer.
  const requestIdentity = request ?? scopeWidenRequest;
  const [lastRequestIdentity, setLastRequestIdentity] = React.useState(() => requestIdentity);
  const [requestVersion, setRequestVersion] = React.useState(0);
  const [lastDataSource, setLastDataSource] = React.useState(() => dataSource);
  const [dataSourceVersion, setDataSourceVersion] = React.useState(0);
  if (lastDataSource !== dataSource) {
    setLastDataSource(dataSource);
    setDataSourceVersion((v) => v + 1);
  }
  if (lastRequestIdentity !== requestIdentity) {
    setLastRequestIdentity(() => requestIdentity);
    setRequestVersion((v) => v + 1);
  }

  // Ohne QueryClientProvider (z. B. Listen im Datenmodus „dataSource“ in Produkten ohne Abfrage-Cache) läuft die
  // Liste mit einem eigenen Cache; Abrufe finden dort ohnehin nicht statt.
  const queryClient = React.useContext(QueryClientContext) ?? standaloneQueryClient;

  const tableRequest = React.useCallback(
    (params?: { current?: number; pageSize?: number }, sort?: Record<string, "ascend" | "descend">) => {
      const currentRequest = requestRef.current;
      // Datenmodus „dataSource“: kein Abruf, die Zeilen kommen direkt aus den Props.
      if (!currentRequest) return Promise.resolve({ data: [] as T[], success: true, total: 0 });
      // `.length` liest die Anzahl der deklarierten Parameter. Der Aufrufer darf `request` wahlweise
      // mit oder ohne Argumente deklarieren; ohne diese Unterscheidung bekäme eine parameterlose
      // Ladefunktion Paginierungsargumente untergeschoben.
      const requestAcceptsParams = (currentRequest as { length: number }).length >= 1;
      if (requestAcceptsParams) {
        return (currentRequest as (params: { current?: number; pageSize?: number }, sort?: Record<string, "ascend" | "descend">) => Promise<{ data: T[]; success: boolean; total: number }>)(params ?? {}, sort);
      }
      return (currentRequest as () => Promise<{ data: T[]; success: boolean; total: number }>)();
    },
    []
  );

  /**
   * Schluessel, unter dem eine Antwort im Query-Cache liegt.
   *
   * `requestVersion` steigt, sobald der Aufrufer eine neue `request`-Closure liefert — also bei
   * jedem Filter-, Bereichs- oder Zustandswechsel der Seite. Damit traegt der Schluessel alles, was
   * das Ergebnis veraendert, ohne dass diese Komponente die Filter der Seite kennen muesste.
   * Ein zu feiner Schluessel kostet einen Abruf, ein zu grober zeigt falsche Daten — deshalb diese
   * Richtung.
   */
  const buildListQueryKey = React.useCallback(
    (current: number, size: number, sort?: Record<string, "ascend" | "descend">) =>
      ["settings-list", storageKey, ...(requestScopeKey === undefined ? [] : [requestScopeKey]), requestVersion, current, size, sort ?? null] as const,
    [storageKey, requestScopeKey, requestVersion]
  );

  /**
   * Fuehrt `tableRequest` ueber den Query-Cache aus: gleiche Anfrage zweimal gleichzeitig ergibt
   * einen Abruf (Dedup), und `invalidateQueries` erreicht den Eintrag. Ersetzt den vorher von Hand
   * gebauten Zwischenspeicher der Kartenliste.
   */
  const cachedTableRequest = React.useCallback(
    (params?: { current?: number; pageSize?: number }, sort?: Record<string, "ascend" | "descend">) => {
      const current = params?.current ?? 1;
      const size = params?.pageSize ?? pageSize;
      return queryClient.fetchQuery({
        queryKey: buildListQueryKey(current, size, sort),
        queryFn: () => tableRequest(params, sort)
      });
    },
    [queryClient, buildListQueryKey, tableRequest, pageSize]
  );

  const sortPersistence = useProTableSortPersistence(
    runtime.accountId,
    `settings.${storageKey}`
  , runtime.sortPreferences);

  const columnEditor = useProTableColumnEditor(
    columnEditorStorageKeyProp ?? `settings.${storageKey}-columns`,
    columnDefinitions,
    allColumnsMap,
    text
  );

  const isNarrow = isBreakpointNarrow;
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const [tableData, setTableData] = React.useState<T[] | null>(null);
  // Grundlage der Breiten- und Aktionsmessung: im Datenmodus „dataSource“ die übergebenen Zeilen.
  const layoutData = dataSourceMode ? (dataSource as T[]) : tableData;
  const tableDataRef = React.useRef<T[]>([]);
  const tableTotalRef = React.useRef(0);
  const [tableTotal, setTableTotal] = React.useState<number | null>(null);
  const tableSnapshotsRef = React.useRef(new Map<string, { data: T[]; total: number }>());
  const requestGenerationRef = React.useRef(0);

  const visibleColumnKeysForOverflow = columnEditor.visibleColumns.map((c) => c.key).join(",");
  const onVisibleColumnsChangeRef = React.useRef(onVisibleColumnsChange);
  onVisibleColumnsChangeRef.current = onVisibleColumnsChange;
  React.useEffect(() => {
    onVisibleColumnsChangeRef.current?.(visibleColumnKeysForOverflow ? visibleColumnKeysForOverflow.split(",") : []);
  }, [visibleColumnKeysForOverflow]);

  const activeActionSlotIndexes = React.useMemo(() => {
    if (!actionSlots?.length || !layoutData?.length) return null;
    const records = collectSettingsListRows(layoutData);
    const active = new Set<number>();
    actionSlots.forEach((slot, index) => {
      if (records.some((record) => slot.show(record))) active.add(index);
    });
    return active;
  }, [actionSlots, layoutData]);
  const activeActionSlotCount = React.useMemo(() => {
    if (!actionSlots?.length) return null;
    if (activeActionSlotIndexes == null || activeActionSlotIndexes.size === 0) return 1;
    return activeActionSlotIndexes.size;
  }, [actionSlots, activeActionSlotIndexes]);
  const actionSlotFullWidths = React.useMemo(() => {
    if (!actionSlots?.length) return null;
    const widths = new Map<number, number>();
    if (activeActionSlotIndexes != null) {
      activeActionSlotIndexes.forEach((index) => widths.set(index, ACTION_BUTTON_ICON_ONLY_WIDTH_PX));
    }
    const records = collectSettingsListRows(layoutData);
    if (records.length === 0) {
      actionSlots.forEach((_, index) => widths.set(index, ACTION_BUTTON_ICON_ONLY_WIDTH_PX));
      return widths;
    }
    actionSlots.forEach((slot, index) => {
      if (activeActionSlotIndexes != null && !activeActionSlotIndexes.has(index)) return;
      for (const record of records) {
        if (!slot.show(record)) continue;
        const label = getButtonLabel(slot.render(record)) ?? "";
        const width = estimateSettingsListActionButtonWidth(label);
        widths.set(index, Math.max(widths.get(index) ?? 0, width));
      }
    });
    return widths;
  }, [actionSlots, activeActionSlotIndexes, layoutData]);
  const columnWidthHints = React.useMemo(() => {
    const widths = new Map<string, number>();
    const records = collectSettingsListRows(layoutData, COLUMN_MEASURED_ROWS_LIMIT);

    columnEditor.visibleColumns.forEach((colDef) => {
      const key = String(colDef.key ?? "");
      const col = (allColumnsMap[key] as ProColumnsWithMeasure<T> | undefined) ?? ({ key, title: key } as ProColumnsWithMeasure<T>);
      let width = estimateHeaderMinWidth(col.title as React.ReactNode);
      if (typeof col.measureDisplayValue === "function") {
        // Explizite Messung: exakter Anzeige-Text pro Zeile (z. B. formatiertes Datum).
        for (const record of records) {
          width = Math.max(width, estimateCellContentWidth(col.measureDisplayValue(record), col.measureExtraPx ?? 0));
        }
      } else if (typeof col.render === "function") {
        // Auto-Messung über den GERENDERTEN Text: viele Spalten bauen ihren Anzeige-Wert per render
        // aus mehreren Feldern (Datums-Bereich, „12,00 €/Monat" …). Der Rohwert eines einzelnen
        // dataIndex wäre zu kurz und würde abschneiden. Fällt der gerenderte Text leer aus (z. B. nur
        // ein Icon), greift der primitive Rohwert als Fallback. Über-Schätzung kann nie abschneiden;
        // die Cap in estimateCellContentWidth begrenzt Ausreißer.
        const renderFn = col.render as (value: unknown, record: T, index: number) => React.ReactNode;
        records.forEach((record, index) => {
          const raw = readSettingsListColumnValue(record, col.dataIndex);
          let segments: string[];
          try {
            // Segmente statt Gesamttext: umbrechbare Tag-Listen (Space/Flex) brauchen nur die Breite des
            // breitesten Tags, nicht die Summe (sonst frisst die Spalte unnötig Platz – siehe „Genutzt von").
            segments = collectSettingsListCellSegments(renderFn(raw, record, index));
          } catch {
            segments = [];
          }
          if (segments.length === 0 && (typeof raw === "string" || typeof raw === "number")) {
            segments = [String(raw)];
          }
          for (const seg of segments) {
            if (seg) width = Math.max(width, estimateCellContentWidth(seg));
          }
        });
      } else if (col.dataIndex != null) {
        // Auto-Messung: primitive Zellwerte (String/Zahl) so breit darstellen, dass z. B. lange Namen
        // nicht abgeschnitten werden. Booleans werden übersprungen (Header-Breite genügt). Über-Schätzung
        // kann nie abschneiden (nur etwas Luft); die Cap in estimateCellContentWidth begrenzt Ausreißer.
        for (const record of records) {
          const raw = readSettingsListColumnValue(record, col.dataIndex);
          if (typeof raw === "string" || typeof raw === "number") {
            const valueText = String(raw);
            if (valueText) width = Math.max(width, estimateCellContentWidth(valueText));
          }
        }
      }
      // Explizite Spaltenbreite (z. B. Combobox) ist eine Untergrenze: SettingsListPage überschreibt
      // `width` später mit Header+Slack, deshalb muss der Wunsch hier in den Hinweis einfließen.
      if (typeof col.width === "number" && col.width > 0) {
        width = Math.max(width, col.width);
      }
      widths.set(key, width);
    });
    return widths;
  }, [allColumnsMap, columnEditor.visibleColumns, layoutData]);
  const visibleDataColumnsMinWidth = React.useMemo(() => {
    return columnEditor.visibleColumns.reduce((sum, colDef) => {
      const key = String(colDef.key ?? "");
      const col = (allColumnsMap[key] as ListColumn<T> | undefined) ?? ({ key, title: key } as ListColumn<T>);
      return sum + estimateHeaderMinWidth(col.title as React.ReactNode);
    }, 0);
  }, [allColumnsMap, columnEditor.visibleColumns]);
  const measuredDataColumnsMinWidth = React.useMemo(() => {
    return columnEditor.visibleColumns.reduce((sum, colDef) => {
      const key = String(colDef.key ?? "");
      const col = (allColumnsMap[key] as ListColumn<T> | undefined) ?? ({ key, title: key } as ListColumn<T>);
      return sum + (columnWidthHints.get(key) ?? estimateHeaderMinWidth(col.title as React.ReactNode));
    }, 0);
  }, [allColumnsMap, columnEditor.visibleColumns, columnWidthHints]);
  const legacyActionLayoutWidths = React.useMemo(() => {
    if (!actionsColumn || actionSlots?.length || !actionsColumn.render) return null;

    const render = actionsColumn.render as (value: unknown, record: T, index: number) => React.ReactNode;
    const records = collectSettingsListRows(layoutData);

    let activeSlotCount = actionsColumn.slotCount ?? 0;
    let fullWidth = typeof actionsColumn.width === "number" ? actionsColumn.width : 0;
    let evaluatedRows = 0;
    let renderThrew = false;
    // Über ALLE Zeilen iterieren (nicht nur die ersten 30): bei bedingten Aktionen kann der EINZIGE Eintrag
    // mit Buttons weit hinten in der Liste stehen (z. B. Vorschau-Beitrag auf Seite 3). Wenn der nicht
    // gesampelt wird, kollabiert die Aktionsspalte fälschlich auf Zahnrad-Breite und der Button überläuft.
    records.forEach((record, index) => {
      try {
        const node = render(undefined, record, index);
        evaluatedRows++;
        const raw = countSettingsListActionNodes(node);
        // Ab vier Aktionen: drei Knöpfe plus Menü (limitSettingsListRowActions).
        const limited = raw > SETTINGS_LIST_MAX_ROW_ACTIONS ? normalizeSettingsListActionItems([node]) : null;
        const labels = collectSettingsListActionButtonLabels(limited ?? node);
        const detectedSlotCount = limited ? limited.length : Math.max(labels.length, raw);
        if (detectedSlotCount <= 0) return;
        activeSlotCount = Math.max(activeSlotCount, detectedSlotCount);
        fullWidth = Math.max(fullWidth, estimateSettingsListActionsWidth(labels.map(estimateSettingsListActionButtonWidth), detectedSlotCount));
      } catch {
        renderThrew = true;
        activeSlotCount = Math.max(activeSlotCount, LEGACY_ACTION_SLOT_FALLBACK_COUNT);
      }
    });

    // Aktionen sind oft bedingt (z. B. nur bei Zeilen mit Exception). Wenn wir Zeilen erfolgreich
    // ausgewertet haben und KEINE Aktion vorkam (kein render-Fehler, kein festes slotCount), darf die
    // Spalte keinen Phantom-Platz für den 2-Slot-Fallback reservieren — sonst entsteht toter Raum
    // rechts, während Datenspalten abschneiden. Sie kollabiert dann auf die Zahnrad-Breite (siehe
    // actionLayoutWidths). Bei render-Fehler oder noch nicht geladenen Daten bleibt der Fallback.
    if (records.length > 0 && evaluatedRows > 0 && activeSlotCount === 0 && !renderThrew) {
      return { activeSlotCount: 0, iconWidth: 0, fullWidth: 0, noActions: true };
    }

    const normalizedSlotCount = Math.max(1, activeSlotCount || LEGACY_ACTION_SLOT_FALLBACK_COUNT);
    const iconWidth = estimateSettingsListIconActionsWidth(normalizedSlotCount);

    return {
      activeSlotCount: normalizedSlotCount,
      iconWidth,
      fullWidth: Math.max(iconWidth, fullWidth || estimateSettingsListActionsWidth([], normalizedSlotCount)),
      noActions: false,
    };
  }, [actionsColumn, actionSlots, layoutData]);
  const actionLayoutWidths = React.useMemo(() => {
    if (!actionsColumn || legacyActionLayoutWidths?.noActions) {
      // Keine Aktionsspalte ODER eine Aktionsspalte, die für die aktuellen Daten keine einzige Aktion
      // rendert: Breite nur fürs Zahnrad reservieren, kein toter Raum.
      const settingsWidth = showColumnEditorProp && columnEditor.settingsButton
        ? settingsOnlyColumnWidthProp ?? SETTINGS_ONLY_COLUMN_WIDTH
        : 0;
      return {
        activeSlotCount: 0,
        iconWidth: settingsWidth,
        fullWidth: settingsWidth,
      };
    }

    const slotCount = actionsColumn.slotCount;
    const activeSlotCount =
      activeActionSlotCount ?? legacyActionLayoutWidths?.activeSlotCount ?? Math.max(1, slotCount ?? actionSlots?.length ?? 1);
    const activeSlotIndexes =
      actionSlots?.length && activeActionSlotIndexes != null && activeActionSlotIndexes.size > 0
        ? Array.from(activeActionSlotIndexes).sort((a, b) => a - b)
        : Array.from({ length: activeSlotCount }, (_, index) => index);
    // Ab vier Aktionen: drei Knöpfe plus Menü (limitSettingsListRowActions).
    const overflowing = activeSlotIndexes.length > SETTINGS_LIST_MAX_ROW_ACTIONS;
    const visibleSlotIndexes = activeSlotIndexes.slice(0, SETTINGS_LIST_MAX_ROW_ACTIONS);
    const visibleSlotCount = overflowing ? SETTINGS_LIST_MAX_ROW_ACTIONS + 1 : activeSlotCount;
    const iconWidth = estimateSettingsListIconActionsWidth(visibleSlotCount);
    const fallbackFullWidth = typeof actionsColumn.width === "number" ? Math.max(iconWidth, actionsColumn.width) : iconWidth;
    const fullWidth = actionSlots?.length
      ? Math.max(iconWidth, estimateSettingsListActionsWidth([...visibleSlotIndexes.map((slotIndex) => actionSlotFullWidths?.get(slotIndex) ?? ACTION_BUTTON_ICON_ONLY_WIDTH_PX), ...(overflowing ? [ACTION_BUTTON_ICON_ONLY_WIDTH_PX] : [])], visibleSlotCount))
      : Math.max(fallbackFullWidth, legacyActionLayoutWidths?.fullWidth ?? 0);

    return {
      activeSlotCount,
      iconWidth: Math.max(iconWidth, legacyActionLayoutWidths?.iconWidth ?? 0),
      fullWidth,
    };
  }, [
    actionsColumn,
    actionSlots,
    activeActionSlotCount,
    activeActionSlotIndexes,
    actionSlotFullWidths,
    showColumnEditorProp,
    columnEditor.settingsButton,
    settingsOnlyColumnWidthProp,
    legacyActionLayoutWidths,
  ]);
  const expandableSummaryColumns = React.useMemo(() => {
    if (!expandableGroup) return [];
    const visibleKeys = new Set(columnEditor.visibleColumns.map((c) => c.key));
    return expandableGroup.summaryColumns.filter(
      (col) => {
        if (col.valueType === VALUE_TYPE_OPTION && !col.columnEditorKey) return true;

        const summaryKey = String(col.key ?? "");
        const editorKey = col.columnEditorKey ?? summaryKey;
        // Gruppenspalten ohne eigenen Eintrag im Spalteneditor sind zusätzliche
        // Summenfelder (z. B. Anzahl je Kunde). Sie dürfen nicht verschwinden,
        // nur weil ihr technischer Schlüssel keiner Detailspalte entspricht.
        const hasColumnEditorDefinition = col.columnEditorKey != null
          || Object.prototype.hasOwnProperty.call(allColumnsMap, editorKey);
        return !hasColumnEditorDefinition || visibleKeys.has(editorKey);
      }
    );
  }, [allColumnsMap, expandableGroup, columnEditor.visibleColumns]);

  const expandableSummaryColumnsWithSettings = React.useMemo(() => {
    if (!expandableGroup || !showColumnEditorProp || !columnEditor.settingsButton || expandableSummaryColumns.length === 0)
      return expandableSummaryColumns;
    const last = expandableSummaryColumns[expandableSummaryColumns.length - 1];
    const origTitle = last?.title;
    return [
      ...expandableSummaryColumns.slice(0, -1),
      {
        ...last,
        title: (
          <Flex justify="flex-end" align="center" style={{ width: "100%", minHeight: 24, minWidth: 0 }}>
            {origTitle != null && origTitle !== "" ? (
              <span style={{ marginRight: "auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {origTitle as React.ReactNode}
              </span>
            ) : null}
            {columnEditor.settingsButton}
          </Flex>
        )
      }
    ];
  }, [expandableGroup, expandableSummaryColumns, columnEditor.settingsButton, showColumnEditorProp]);

  const expandableSummaryColumnWidths = React.useMemo(() => {
    if (!expandableGroup) return new Map<string, { full: number; icon: number }>();

    const widths = new Map<string, { full: number; icon: number }>();
    expandableSummaryColumnsWithSettings.forEach((col, columnIndex) => {
      const key = String(col.key ?? columnIndex);
      const headerWidth = estimateHeaderMinWidth(col.title as React.ReactNode);
      const configuredWidth = Math.max(
        typeof col.width === "number" ? col.width : 0,
        typeof col.minWidth === "number" ? col.minWidth : 0
      );
      const baseWidth = Math.max(headerWidth, configuredWidth);
      if (col.valueType !== VALUE_TYPE_OPTION || typeof col.render !== "function") {
        widths.set(key, { full: baseWidth, icon: baseWidth });
        return;
      }

      const render = col.render as (
        value: unknown,
        record: Record<string, unknown>,
        index: number
      ) => React.ReactNode;
      let activeActionCount = 0;
      let measuredFullWidth = baseWidth;
      (expandableGroup.data ?? []).forEach((record, index) => {
        try {
          const rendered = render(undefined, record, index);
          const labels = collectSettingsListActionButtonLabels(rendered);
          const actionCount = Math.max(labels.length, countSettingsListActionNodes(rendered));
          if (actionCount <= 0) return;
          activeActionCount = Math.max(activeActionCount, actionCount);
          const buttonWidths = Array.from(
            { length: actionCount },
            (_, actionIndex) => estimateSettingsListActionButtonWidth(labels[actionIndex])
          );
          measuredFullWidth = Math.max(
            measuredFullWidth,
            estimateSettingsListActionsWidth(buttonWidths, actionCount)
          );
        } catch {
          const fallbackActionCount = Math.max(
            1,
            (col as ListColumn<Record<string, unknown>> & { slotCount?: number }).slotCount ?? LEGACY_ACTION_SLOT_FALLBACK_COUNT
          );
          activeActionCount = Math.max(activeActionCount, fallbackActionCount);
          measuredFullWidth = Math.max(
            measuredFullWidth,
            estimateSettingsListActionsWidth([], fallbackActionCount)
          );
        }
      });

      const normalizedActionCount = Math.max(
        1,
        activeActionCount,
        (col as ListColumn<Record<string, unknown>> & { slotCount?: number }).slotCount ?? 0
      );
      widths.set(key, {
        full: measuredFullWidth,
        icon: Math.max(headerWidth, estimateSettingsListIconActionsWidth(normalizedActionCount)),
      });
    });
    return widths;
  }, [expandableGroup, expandableSummaryColumnsWithSettings]);

  const structuralColumnMinWidth = React.useMemo(() => {
    const effectiveSelection = expandableGroup?.rowSelection ?? rowSelectionProp;
    const selectionWidth = effectiveSelection ? effectiveSelection.columnWidth ?? SETTINGS_LIST_SELECTION_COLUMN_WIDTH : 0;
    const hasTreeExpandColumn = expandableGroup
      ? true
      : layoutData?.some((item) => getSettingsListNestedRows(item, "children").length > 0);
    const treeExpandWidth = hasTreeExpandColumn ? SETTINGS_LIST_TREE_EXPAND_COLUMN_WIDTH : 0;
    return selectionWidth + treeExpandWidth;
  }, [expandableGroup, rowSelectionProp, layoutData]);
  // Header sind das harte Limit: passt schon der Header nicht (Header-Summe + Aktion + Struktur > Container),
  // wird auf Kartenansicht umgeschaltet. Spaltentitel dürfen nie abgeschnitten werden – sie definieren die
  // minimal nötige Tabellenbreite. Die gemessenen Inhaltsbreiten fließen NICHT in diese Schwelle ein; sie
  // steuern nur, ob Zelleninhalte in voller Breite gezeigt werden oder per Ellipsis (siehe baseColumns).
  const groupedSummaryMinWidths = React.useMemo(() => {
    if (!expandableGroup) return null;
    return expandableSummaryColumnsWithSettings.reduce(
      (totals, col, columnIndex) => {
        const key = String(col.key ?? columnIndex);
        const widths = expandableSummaryColumnWidths.get(key);
        const fallback = estimateHeaderMinWidth(col.title as React.ReactNode);
        totals.full += widths?.full ?? fallback;
        totals.icon += widths?.icon ?? fallback;
        return totals;
      },
      {
        full: structuralColumnMinWidth,
        icon: structuralColumnMinWidth,
      }
    );
  }, [expandableGroup, expandableSummaryColumnsWithSettings, expandableSummaryColumnWidths, structuralColumnMinWidth]);
  const dataColumnsMinWidth = visibleDataColumnsMinWidth;
  const fullTableMinWidth =
    groupedSummaryMinWidths?.full ??
    (dataColumnsMinWidth + structuralColumnMinWidth + actionLayoutWidths.fullWidth + TABLE_MODE_SWITCH_GUARD_PX +
      (actionsColumn ? TABLE_FULL_ACTION_MODE_SAFETY_PX : 0));
  const iconTableMinWidth =
    groupedSummaryMinWidths?.icon ??
    (dataColumnsMinWidth + structuralColumnMinWidth + actionLayoutWidths.iconWidth + TABLE_MODE_SWITCH_GUARD_PX);
  const tableActionsEnabled =
    Boolean(actionsColumn) || expandableSummaryColumnsWithSettings.some((col) => col.valueType === VALUE_TYPE_OPTION);
  const {
    useCardView,
    availableWidth,
  } = useSettingsListTableOverflowMeasure({
    wrapperRef: tableWrapperRef,
    actionsEnabled: tableActionsEnabled,
    fullMinWidth: fullTableMinWidth,
    iconMinWidth: iconTableMinWidth,
    columnLayoutKey: visibleColumnKeysForOverflow,
    dataDependency: expandableGroup ? expandableGroup.data : layoutData,
    isNarrowViewport: isNarrow,
  });
  const allowCardViewForViewport = !preserveDesktopTable || isNarrow;
  const useCardViewEffective = expandableGroup || disableCardView ? false : useCardView && allowCardViewForViewport;
  // Flache Listen rendern in allen Produkten als Zeilenliste ohne ProTable (vorher Produktwahl über
  // `runtime.presentation`, entfallen 13.10.2026); gestapelte Karten übernehmen den Kartenwechsel, auch
  // dort, wo die Tabellenkarte abgeschaltet ist. Gruppierte Listen bleiben bis zur gemeinsamen
  // Liste (Plan 2026-10-13-antd-ausbau, Stufe 3) bei ProTable.
  const useRowsPresentation = true;
  // Platzstufe aus der Messung des Rasters (SettingsListRows): Titel ungekürzt plus Aktionen mit Text,
  // sonst Aktionen nur als Symbol, sonst Karten. Unterhalb des Kartenbreakpoints immer Karten.
  const [rowsFit, setRowsFit] = React.useState<SettingsListRowsFit>("full");
  const rowsStacked = useRowsPresentation && (isNarrow || rowsFit === "stacked");

  const buildActionItemsFromSlots = React.useCallback(
    (record: T, includePlaceholders = true, iconOnlyPlaceholders = false): (React.ReactNode | null)[] => {
      if (!actionSlots?.length) return [];
      return actionSlots.flatMap((slot, i) => {
        const node = slot.render(record);
        if (slot.show(record)) {
          if (React.isValidElement(node)) {
            return [React.cloneElement(node, { key: `slot-${i}` })];
          }
          return [<React.Fragment key={`slot-${i}`}>{node}</React.Fragment>];
        }
        if (!includePlaceholders) return [];
        if (activeActionSlotIndexes != null && !activeActionSlotIndexes.has(i)) return [];
        if (React.isValidElement(node) && typeof node.type !== "string") {
          const el = node as React.ReactElement<{ style?: React.CSSProperties; disabled?: boolean }>;
          const iconOnly = iconOnlyPlaceholders ? withIconOnlyActions(el) : el;
          const placeholder = React.isValidElement(iconOnly) ? iconOnly : el;
          return [(
            <React.Fragment key={`slot-${i}`}>
              {React.cloneElement(placeholder as React.ReactElement<{ style?: React.CSSProperties; disabled?: boolean }>, {
                disabled: true,
                style: { ...(placeholder as React.ReactElement<{ style?: React.CSSProperties }>).props?.style, visibility: "hidden", pointerEvents: "none" },
              })}
            </React.Fragment>
          )];
        }
        return [<span key={`slot-${i}`} style={{ visibility: "hidden", display: "inline-block" }} aria-hidden />];
      });
    },
    [actionSlots, activeActionSlotIndexes]
  );

  // In Themes mit `listActions: "icon"` stehen Aktionen immer nur als Symbol.
  const themeIconOnlyActions = useOptionalAppTheme()?.theme.listActions === "icon";
  const iconOnlyActions = themeIconOnlyActions || rowsFit !== "full";
  // Aktionszelle in beiden Darstellungen: für die Spalte und für die Breitenmessung des Rasters.
  const renderActionsCell = React.useCallback(
    (value: unknown, record: T, index: number, iconOnlyRequested: boolean): React.ReactElement | null => {
      if (!actionsColumn) return null;
      const iconOnly = themeIconOnlyActions || iconOnlyRequested;
      const origRender = actionsColumn.render;
      const slotCount = actionsColumn.slotCount;
      const activeSlotCount = actionLayoutWidths.activeSlotCount || Math.max(1, slotCount ?? actionSlots?.length ?? 1);
      let raw: (React.ReactNode | null)[] = [];
      if (actionSlots?.length) {
        raw = buildActionItemsFromSlots(record, true, iconOnly);
      } else if (origRender) {
        const content = (origRender as (v: unknown, r: T, i: number) => React.ReactNode)(value, record, index);
        raw = Array.isArray(content) ? content : content ? [content] : [];
        if (slotCount != null && raw.length < slotCount) {
          const filled = raw;
          raw = Array.from({ length: slotCount }, (_, i) => filled[i] ?? null);
        }
      }
      const template = raw.find((x) => x != null && React.isValidElement(x) && typeof (x as React.ReactElement).type !== "string");
      const withPlaceholders = raw.map((x, i) => {
        if (x != null) return x;
        if (template != null && React.isValidElement(template)) {
          const el = template as React.ReactElement<{ style?: React.CSSProperties; disabled?: boolean }>;
          const iconOnlyTemplate = iconOnly ? withIconOnlyActions(el) : el;
          const placeholder = React.isValidElement(iconOnlyTemplate) ? iconOnlyTemplate : el;
          return (
            <React.Fragment key={`ph-${i}`}>
              {React.cloneElement(placeholder as React.ReactElement<{ style?: React.CSSProperties; disabled?: boolean }>, {
                disabled: true,
                style: { ...(placeholder as React.ReactElement<{ style?: React.CSSProperties }>).props?.style, visibility: "hidden", pointerEvents: "none" },
              })}
            </React.Fragment>
          );
        }
        return <span key={`ph-${i}`} style={{ visibility: "hidden", display: "inline-block" }} aria-hidden />;
      });
      const expectedCount = actionSlots?.length ? activeSlotCount : slotCount;
      const normalizedItems = normalizeSettingsListActionItems(withPlaceholders, expectedCount);
      return renderSettingsListStyleActionCell(normalizedItems, iconOnly, actionsCellLayout);
    },
    [actionsColumn, actionSlots, actionLayoutWidths.activeSlotCount, buildActionItemsFromSlots, actionsCellLayout, themeIconOnlyActions]
  );
  const actionsCellClassName = React.useCallback(
    (iconOnlyRequested: boolean) =>
      [
        (actionsColumn?.className ?? "").trim(),
        "settings-list-actions-cell",
        themeIconOnlyActions || iconOnlyRequested ? "settings-list-actions-cell-icon-only" : "",
        actionsCellLayout === "vertical" ? "settings-list-actions-vertical-cell" : "",
      ]
        .filter(Boolean)
        .join(" ")
        .trim(),
    [actionsColumn?.className, actionsCellLayout, themeIconOnlyActions]
  );
  const actionProbe = React.useMemo<SettingsListRowsActionProbe<T> | undefined>(() => {
    if (!actionsColumn || !(actionSlots?.length || actionsColumn.render)) return undefined;
    return {
      render: (record, index, iconOnly) =>
        renderActionsCell(getSettingsListValueByPath(record, settingsListDataIndexPath(actionsColumn.dataIndex)), record, index, iconOnly),
      className: actionsCellClassName,
    };
  }, [actionsColumn, actionSlots, renderActionsCell, actionsCellClassName]);
  const baseColumns = React.useMemo(() => {
    const visibleCols = columnEditor.visibleColumns;
    const actionsWidthForFit = iconOnlyActions ? actionLayoutWidths.iconWidth : actionLayoutWidths.fullWidth;
    // Slack-Verteilung: Browser würde den Restplatz proportional auf ALLE Spalten verteilen, inkl. der
    // Aktionsspalte – diese bekäme so viel Leerraum, während Datenspalten ellipsieren. Wir verteilen
    // selbst und NUR auf Datenspalten. Zusätzlich gilt: Eine kurze Spalte (z. B. formatiertes Datum)
    // braucht keinen Aufschlag – das würde nur Leerraum erzeugen. Slack geht daher BEVORZUGT an Spalten,
    // die das Inhalts-Cap erreicht haben (Inhalt war länger als die 360px-Obergrenze → die Spalte will
    // wirklich mehr Platz). Gibt es keine solche Spalte, wird der Slack gleichmäßig verteilt.
    // Einheitliche Basis: immer Header-Breite. Slack fließt proportional zum Defizit (= zusätzlicher
    // Inhalt jenseits des Headers). Damit gibt es KEINEN Modus-Wechsel mehr zwischen "Inhalt passt" und
    // "Inhalt passt nicht" – beim Resizen ändern sich die Breiten gleichmäßig stetig statt sprunghaft
    // (sonst flatterten die Spaltenköpfe beim Fenster-Verschieben).
    const isCappedPerCol: boolean[] = visibleCols.map((colDef) => {
      const k = String(colDef.key ?? "");
      const hint = columnWidthHints.get(k);
      return hint != null && hint >= COLUMN_MEASURED_WIDTH_CAP_PX - 1;
    });
    const cappedCount = isCappedPerCol.filter(Boolean).length;
    const dataBasePerCol: number[] = visibleCols.map((colDef) => {
      const k = String(colDef.key ?? "");
      const c = allColumnsMap[k] as ProColumnsWithMeasure<T> | undefined;
      return estimateHeaderMinWidth(c?.title as React.ReactNode);
    });
    const sumDataBase = dataBasePerCol.reduce((a, b) => a + b, 0);
    const dataSlack = availableWidth > 0
      ? Math.max(0, availableWidth - sumDataBase - actionsWidthForFit - structuralColumnMinWidth)
      : 0;
    // Pro Spalte: das "Defizit" = wie viel zwischen aktueller Basis und gewünschter Inhaltsbreite fehlt.
    // Spalten, deren Inhalt schon in die Basis passt (Modell/Grund/Pauschale = "—"), haben Defizit 0 →
    // bekommen keinen Slack-Aufschlag (sonst nur Leerraum). Spalten, die abgeschnitten würden (Periode,
    // Vertrag, Optionen), haben positives Defizit und teilen sich den Slack proportional dazu.
    const deficitPerCol: number[] = visibleCols.map((colDef, i) => {
      const k = String(colDef.key ?? "");
      const fullHint = columnWidthHints.get(k) ?? dataBasePerCol[i];
      return Math.max(0, fullHint - dataBasePerCol[i]);
    });
    const sumDeficit = deficitPerCol.reduce((a, b) => a + b, 0);
    let slackSharePerCol: number[] = dataBasePerCol.map(() => 0);
    let remainingSlack = dataSlack;
    if (remainingSlack > 0 && sumDeficit > 0) {
      // Slack proportional auf die "wollen-mehr-Spalten" verteilen, höchstens bis ihr Defizit gedeckt ist.
      const factor = Math.min(1, remainingSlack / sumDeficit);
      slackSharePerCol = deficitPerCol.map((d) => d * factor);
      remainingSlack = Math.max(0, remainingSlack - slackSharePerCol.reduce((a, b) => a + b, 0));
    }
    if (remainingSlack > 0) {
      if (cappedCount > 0) {
        // Reststeppe: Cap-Spalten wollen MEHR als 360px (Inhalt überschreitet das Cap) – sie kriegen alles.
        const perCapped = remainingSlack / cappedCount;
        slackSharePerCol = slackSharePerCol.map((s, i) => s + (isCappedPerCol[i] ? perCapped : 0));
      } else if (dataBasePerCol.length > 0) {
        // Keine Spalte will mehr (alle ≥ ihr Inhalt). Restplatz gleichmäßig auf alle Datenspalten verteilen.
        const perCol = remainingSlack / dataBasePerCol.length;
        slackSharePerCol = slackSharePerCol.map((s) => s + perCol);
      }
    }
    // Floor + Rundungsrest auf die erste Spalte schlagen, damit Σ widths == verfügbarer Datenplatz.
    if (dataSlack > 0 && slackSharePerCol.length > 0) {
      const floored = slackSharePerCol.map((s) => Math.floor(s));
      const lost = slackSharePerCol.reduce((a, b) => a + b, 0) - floored.reduce((a, b) => a + b, 0);
      const targetIdx = isCappedPerCol.findIndex(Boolean);
      const recipientIdx = targetIdx >= 0 ? targetIdx : 0;
      floored[recipientIdx] = floored[recipientIdx] + Math.round(lost);
      slackSharePerCol = floored;
    }
    /** Liefert {fromIdx, toIdx} fuer eine Zeile, falls ein Overlay aktiv ist; sonst null. Indizes beziehen sich auf `visibleCols`. */
    const getOverlayRange = (record: T): { fromIdx: number; toIdx: number; content: React.ReactNode } | null => {
      const ov = effectiveRowOverlay(record);
      if (!ov) return null;
      if (ov.spanVisibleDataColumns) {
        const reserveFirst = Math.max(0, ov.reserveFirstVisibleColumnCount ?? 0);
        const reserveLast = Math.max(0, ov.reserveLastVisibleColumnCount ?? 0);
        const excludedKeys = new Set((ov.excludedColumnKeys ?? []).map(String));
        const candidates = visibleCols
          .map((c, index) => ({ key: String(c.key ?? ""), index }))
          .slice(reserveFirst, Math.max(reserveFirst, visibleCols.length - reserveLast));
        const ranges: Array<{ fromIdx: number; toIdx: number; length: number }> = [];
        let currentRangeStart: number | null = null;
        let currentRangeEnd: number | null = null;
        const commitCurrentRange = () => {
          if (currentRangeStart == null || currentRangeEnd == null) return;
          ranges.push({
            fromIdx: currentRangeStart,
            toIdx: currentRangeEnd,
            length: currentRangeEnd - currentRangeStart + 1
          });
          currentRangeStart = null;
          currentRangeEnd = null;
        };
        for (const candidate of candidates) {
          if (!candidate.key || excludedKeys.has(candidate.key)) {
            commitCurrentRange();
            continue;
          }
          if (currentRangeStart == null) {
            currentRangeStart = candidate.index;
            currentRangeEnd = candidate.index;
          } else {
            currentRangeEnd = candidate.index;
          }
        }
        commitCurrentRange();
        const bestRange = ranges.reduce<{ fromIdx: number; toIdx: number; length: number } | null>(
          (best, range) => (best == null || range.length > best.length ? range : best),
          null
        );
        if (!bestRange) return null;
        return { fromIdx: bestRange.fromIdx, toIdx: bestRange.toIdx, content: ov.content };
      }
      if (!ov.spanFromColumnKey || !ov.spanToColumnKey) return null;
      const fromIdx = visibleCols.findIndex((c) => c.key === ov.spanFromColumnKey);
      const toIdx = visibleCols.findIndex((c) => c.key === ov.spanToColumnKey);
      if (fromIdx < 0 || toIdx < 0 || toIdx < fromIdx) return null;
      return { fromIdx, toIdx, content: ov.content };
    };
    const cols: ListColumn<T>[] = visibleCols.map((colDef, colIdx) => {
      const key = String(colDef.key ?? "");
      const fullCol = allColumnsMap[key] as ListColumn<T> | undefined;
      const col = fullCol ?? ({ key, title: key } as ListColumn<T>);
      const existingOnCell = col.onCell;
      const origRender = col.render;
      const hasDataIndex = col.dataIndex != null && (Array.isArray(col.dataIndex) ? col.dataIndex.length > 0 : true);
      const baseSafeRender: (value: unknown, record: T, index: number, action?: unknown) => React.ReactNode =
        origRender != null
          ? (value, record, index, action) => safeSettingsListCellNode((origRender as (value: unknown, record: T, index: number, action?: unknown) => unknown)(value, record, index, action))
          : hasDataIndex
            ? (value) => safeSettingsListCellNode(value ?? "")
            : () => "—";
      const columnHeaderHint = estimateHeaderMinWidth(col.title as React.ReactNode);
      const columnMinWidth = columnWidthHints.get(key) ?? columnHeaderHint;
      const safeRender: ListColumn<T>["render"] = (value, record, index, action) => {
        const range = getOverlayRange(record);
        if (range) {
          if (colIdx === range.fromIdx) return safeSettingsListCellNode(range.content);
          if (colIdx > range.fromIdx && colIdx <= range.toIdx) return null;
        }
        return baseSafeRender(value, record, index, action);
      };
      return {
        ...col,
        key,
        // Echte colgroup-Breite: Basis = Inhalts-Breite (oder Header, falls Inhalt nicht passt) plus
        // ein gezielter Slack-Anteil. Die Aktions-/Strukturspalte bekommt keinen Slack, und der Slack
        // wird bevorzugt an Cap-Spalten (Inhalt > 360px) gegeben – kurze Spalten (z. B. Datum) bleiben
        // tight am Inhalt statt Leerraum aufzubauen (siehe slackSharePerCol oben).
        width: Math.round(columnHeaderHint + (slackSharePerCol[colIdx] ?? 0)),
        // Untergrenze der Spalte ist ihr gemessener Titel (SettingsListRows); hier nur ausdrückliche Wünsche.
        minWidth: typeof col.minWidth === "number" ? col.minWidth : 0,
        // Darstellung "rows": Platzverteilung nach gemessener Inhaltsbreite (Kopf als Untergrenze).
        contentWidth: Math.max(columnHeaderHint, columnMinWidth),
        ellipsis: col.ellipsis !== false,
        render: safeRender,
        onCell: (record: T, rowIndex?: number) => {
          const existing = existingOnCell?.(record, rowIndex ?? 0) ?? {};
          // Span-Zelle braucht den 200-px-MaxWidth nicht und auch keinen Wrap-Cut, sonst wird der Inhalt hart abgeschnitten.
          const range = getOverlayRange(record);
          const isOverlaySpanFrom = range != null && colIdx === range.fromIdx;
          const isOverlayCollapsed = range != null && colIdx > range.fromIdx && colIdx <= range.toIdx;
          const cellStyle = isOverlaySpanFrom
            ? { ...(existing.style ?? {}) }
            : {
                ...WRAP_CELL_STYLE,
                minWidth: columnMinWidth,
                ...(col.ellipsis === false ? { overflow: "visible" as const } : {}),
                ...(existing.style ?? {}),
              };
          const cellClassName = isOverlaySpanFrom
            ? [existing.className].filter(Boolean).join(" ")
            : [existing.className, "settings-list-wrap-cell"].filter(Boolean).join(" ");
          const base: Record<string, unknown> = {
            ...existing,
            className: cellClassName,
            style: cellStyle,
          };
          if (isOverlaySpanFrom && range) {
            base.colSpan = range.toIdx - range.fromIdx + 1;
          } else if (isOverlayCollapsed) {
            base.colSpan = 0;
          }
          if (rowSelectionProp && onRowClickForSelection) {
            base.onClick = (e: React.MouseEvent<HTMLTableCellElement>) => {
              onRowClickForSelection(record, e);
              e.stopPropagation();
            };
          }
          return base;
        },
      };
    });

    if (actionsColumn) {
      const origRender = actionsColumn.render;
      const actionWidth = iconOnlyActions ? actionLayoutWidths.iconWidth : actionLayoutWidths.fullWidth;
      const actionMinWidth = actionWidth;
      const renderCell = (value: unknown, record: T, index: number) => renderActionsCell(value, record, index, iconOnlyActions);
      const actionsClassName = actionsCellClassName(iconOnlyActions);

      // Aktionsspalte: feste Breite je Modus. Nur Daten-Spalten dürfen beim Ziehen flexen.
      cols.push({
        ...actionsColumn,
        align: "right" as const,
        fixed: fixActionsColumn ? "right" as const : undefined,
        width: actionWidth,
        minWidth: actionMinWidth,
        ellipsis: false as const,
        onCell: (record, rowIndex) => {
          const base = (typeof actionsColumn.onCell === "function" ? actionsColumn.onCell(record, rowIndex) : undefined) ?? {};
          const className = [base.className, !fixActionsColumn ? "settings-list-actions-column-no-fix" : ""]
            .filter(Boolean)
            .join(" ")
            .trim();
          const style: React.CSSProperties = {
            ...(base.style as React.CSSProperties | undefined),
            width: actionWidth,
            minWidth: actionMinWidth,
            overflow: iconOnlyActions ? "hidden" : "visible",
            maxWidth: "none",
            whiteSpace: "nowrap",
            wordBreak: "normal" as const,
          };
          const next = { ...base } as typeof base & { className?: string; style?: React.CSSProperties };
          if (className) next.className = className;
          if (Object.keys(style).length > 0) next.style = style;
          return next;
        },
        className: actionsClassName || undefined,
        render: (actionSlots?.length ? true : origRender) ? renderCell : undefined,
      });
    } else if (showColumnEditorProp && columnEditor.settingsButton) {
      const settingsWidth = settingsOnlyColumnWidthProp ?? SETTINGS_ONLY_COLUMN_WIDTH;
      cols.push({
        key: "settings",
        title: columnEditor.settingsButton,
        fixed: undefined,
        align: "right" as const,
        minWidth: settingsWidth,
        width: settingsWidth,
        render: () => null,
      });
    }

    return cols;
  }, [
    columnEditor.visibleColumns,
    allColumnsMap,
    columnWidthHints,
    availableWidth,
    structuralColumnMinWidth,
    measuredDataColumnsMinWidth,
    actionsColumn,
    actionSlots,
    actionLayoutWidths,
    renderActionsCell,
    actionsCellClassName,
    iconOnlyActions,
    columnEditor.settingsButton,
    showColumnEditorProp,
    fixActionsColumn,
    rowSelectionProp,
    onRowClickForSelection,
    effectiveRowOverlay,
  ]);

  const columns: ListColumn<T>[] = React.useMemo(() => {
    const sorted = sortPersistence.makeColumnsSortable(baseColumns, dataMode === "server-query" || callerSortsOnServer);

    if (showColumnEditorProp && columnEditor.settingsButton && sorted.length > 0) {
      const lastIdx = sorted.length - 1;
      const last = sorted[lastIdx];
      const lastKey = String(last?.key ?? "");
      const hasSeparateSettingsCol = sorted.some((c) => String(c?.key ?? "") === SETTINGS_COLUMN_KEY);
      const isActionsCol = lastKey === ACTIONS_COLUMN_KEY || lastKey.startsWith(ACTIONS_COLUMN_KEY_PREFIX);
      const isSettingsCol = lastKey === SETTINGS_COLUMN_KEY;
      const isActionsOrSettingsCol = isActionsCol || isSettingsCol;
      // Wenn Aktionen die letzte Spalte sind und es eine eigene Zahnrad-Spalte gibt: Titel der Aktionen-Spalte nicht mit Zahnrad ersetzen
      const mergeGearIntoLast = isActionsOrSettingsCol
        ? !(isActionsCol && hasSeparateSettingsCol)
        : true;
      const gearWithOptionalExtra = listHeaderExtraLeft ? (
        <Flex align="center" justify="flex-end" gap={8} style={{ flexShrink: 0, width: "100%" }}>
          {listHeaderExtraLeft}
          {columnEditor.settingsButton}
        </Flex>
      ) : columnEditor.settingsButton;
      sorted[lastIdx] = {
        ...last,
        title: !mergeGearIntoLast
          ? (last?.title ?? "")
          : isActionsOrSettingsCol
            ? gearWithOptionalExtra
            : ((
                <Flex justify="flex-end" align="center" style={{ width: "100%", minHeight: 24, minWidth: 0 }}>
                  {last.title != null && last.title !== "" ? (
                    <span
                      style={{
                        marginRight: "auto",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {last.title as React.ReactNode}
                    </span>
                  ) : null}
                  {gearWithOptionalExtra}
                </Flex>
              ) as React.ReactNode),
      };
    }

    return sorted;
  }, [baseColumns, callerSortsOnServer, columnEditor.settingsButton, dataMode, showColumnEditorProp, listHeaderExtraLeft, sortPersistence.makeColumnsSortable]);

  // --- Toolbar ---
  const toolbarButtons = React.useMemo(() => {
    const buttons: React.ReactNode[] = [...toolBarActions];
    if (onAdd) {
      buttons.push(
        <Button key="add" type="primary" icon={<PlusOutlined />} onClick={onAdd} disabled={addButtonDisabled}>
          {text(addButtonText)}
        </Button>
      );
    }
    return buttons;
  }, [onAdd, addButtonText, addButtonDisabled, toolBarActions, text]);

  // Kartenansicht ohne Tabellenkopf: Zahnrad samt `listHeaderExtraLeft` wandern in die Werkzeugleiste.
  const cardToolbarButtons = React.useMemo(() => {
    if (!showColumnEditorProp || !columnEditor.settingsButton) return toolbarButtons;
    return [
      ...toolbarButtons,
      ...(listHeaderExtraLeft ? [<React.Fragment key="list-header-extra-left">{listHeaderExtraLeft}</React.Fragment>] : []),
      <React.Fragment key="column-settings">
        {columnEditor.settingsButton}
      </React.Fragment>
    ];
  }, [columnEditor.settingsButton, listHeaderExtraLeft, showColumnEditorProp, toolbarButtons]);

  const rowKeyFn = React.useCallback(
    (record: T) => {
      return typeof rowKey === "function"
        ? rowKey(record)
        : String((record as Record<string, unknown>)[String(rowKey)]);
    },
    [rowKey]
  );

  const buildRowClassName = React.useCallback(
    (record: T) => {
      const classes = [rowClassNameProp?.(record)];
      if (activeRowKey != null && String(rowKeyFn(record)) === String(activeRowKey)) {
        classes.push("settings-list-row-active");
      }
      return classes.filter(Boolean).join(" ").trim();
    },
    [activeRowKey, rowClassNameProp, rowKeyFn]
  );

  const expandableChildColumns = React.useMemo(() => {
    if (!expandableGroup) return [];
    const visibleKeys = new Set(columnEditor.visibleColumns.map((c) => c.key));
    const childColumns = expandableGroup.childColumns ?? expandableGroup.childColumnKeys
      .filter((key) => visibleKeys.has(key))
      .map((key) => allColumnsMap[key])
      .filter(Boolean);
    return childColumns.map((col) => ({
        title: col!.title,
        dataIndex: col!.dataIndex,
        key: col!.key,
        width: col!.width,
        align: col!.align,
        ellipsis: col!.ellipsis,
        valueType: col!.valueType,
        // Die Kindtabelle hat eine eigene Ant-Instanz ohne Sortier-Persistenz. Ohne diesen
        // abgeleiteten Vergleich waeren dort auch die Spalten unsortierbar, die in der
        // Hauptliste sortierbar sind.
        sorter: deriveColumnSorter(col!) ?? undefined,
        defaultSortOrder: col!.defaultSortOrder,
        showSorterTooltip: col!.showSorterTooltip,
        render: col!.render
      }));
  }, [expandableGroup, columnEditor.visibleColumns, allColumnsMap]);

  const useIconOnlyInExpandableSummary = React.useMemo(() => {
    if (!expandableGroup || availableWidth <= 0) return false;
    const fullWidth = expandableSummaryColumnsWithSettings.reduce((sum, col, columnIndex) => {
      const key = String(col.key ?? columnIndex);
      return sum + (expandableSummaryColumnWidths.get(key)?.full ?? estimateHeaderMinWidth(col.title as React.ReactNode));
    }, SETTINGS_LIST_TREE_EXPAND_COLUMN_WIDTH);
    return fullWidth > availableWidth;
  }, [availableWidth, expandableGroup, expandableSummaryColumnsWithSettings, expandableSummaryColumnWidths]);

  const expandableSummaryColumnsForTable = React.useMemo(() => {
    if (!expandableGroup) return expandableSummaryColumnsWithSettings;
    // A full-width fixed-layout table with only explicit column widths distributes
    // spare space across every column, including expand and selection. Leave one
    // content column fluid so the structural columns retain their declared widths.
    const fluidColumnIndex = expandableSummaryColumnsWithSettings.reduce(
      (lastIndex, column, index) => column.valueType !== VALUE_TYPE_OPTION ? index : lastIndex,
      -1
    );
    return expandableSummaryColumnsWithSettings.map((col, columnIndex) => {
      const key = String(col.key ?? columnIndex);
      const measuredWidths = expandableSummaryColumnWidths.get(key);
      const summaryColumnMinWidth = col.valueType === VALUE_TYPE_OPTION && useIconOnlyInExpandableSummary
        ? measuredWidths?.icon ?? ACTION_ICON_ONLY_MIN_WIDTH
        : measuredWidths?.full ?? estimateHeaderMinWidth(col.title as React.ReactNode);
      const existingHeaderCell = col.onHeaderCell as ((column: unknown) => React.ThHTMLAttributes<HTMLElement>) | undefined;
      const withSummaryHeader = {
        ...col,
        width: columnIndex === fluidColumnIndex ? undefined : summaryColumnMinWidth,
        minWidth: summaryColumnMinWidth,
        onHeaderCell: (column: unknown) => {
          const base = (typeof existingHeaderCell === "function" ? existingHeaderCell(column) : {}) as React.ThHTMLAttributes<HTMLElement>;
          return {
            ...base,
            style: {
              ...(base.style ?? {}),
              minWidth: summaryColumnMinWidth,
              whiteSpace: "nowrap",
            } as React.CSSProperties,
          };
        },
      };
      if (col.valueType !== VALUE_TYPE_OPTION) return withSummaryHeader;
      const origRender = col.render;
      return {
        ...withSummaryHeader,
        ellipsis: false as const,
        className: [col.className, "settings-list-actions-cell", useIconOnlyInExpandableSummary ? "settings-list-actions-cell-icon-only" : ""]
          .filter(Boolean)
          .join(" "),
        onCell: (record: Record<string, unknown>, rowIndex?: number) => {
          const base = (typeof col.onCell === "function" ? col.onCell(record, rowIndex ?? 0) : undefined) ?? {};
          return {
            ...base,
            className: [base.className, "settings-list-actions-cell", useIconOnlyInExpandableSummary ? "settings-list-actions-cell-icon-only" : ""]
              .filter(Boolean)
              .join(" "),
            style: {
              ...(base.style ?? {}),
              whiteSpace: "nowrap",
              maxWidth: "none",
              overflow: useIconOnlyInExpandableSummary ? "hidden" : "visible",
            } as React.CSSProperties,
          };
        },
        render: origRender
          ? (value: unknown, record: Record<string, unknown>, index: number) => {
              const rendered = (origRender as (value: unknown, record: Record<string, unknown>, index: number) => React.ReactNode)(
                value,
                record,
                index
              );
              const normalized = normalizeSettingsListActionItems(Array.isArray(rendered) ? rendered : [rendered]);
              return renderSettingsListStyleActionCell(normalized, useIconOnlyInExpandableSummary);
            }
          : undefined,
      } as ListColumn<object>;
    });
  }, [expandableGroup, expandableSummaryColumnsWithSettings, expandableSummaryColumnWidths, useIconOnlyInExpandableSummary]);

  // Nur gruppierte Listen: der Aufruf setzt die Systemsortierung und würde sie sonst mit leeren
  // Gruppenspalten für flache Listen löschen (Systemsortierung griff dann erst nach einem Nachladen).
  const expandableSummarySortableColumns = React.useMemo(
    () => expandableGroup ? sortPersistence.makeColumnsSortable(expandableSummaryColumnsForTable) : [],
    [expandableGroup, expandableSummaryColumnsForTable, sortPersistence.makeColumnsSortable]
  );

  const groupedTableRequest = React.useCallback((_params?: { current?: number; pageSize?: number }, sort?: Record<string, "ascend" | "descend">) => {
    const data = expandableGroup?.data ?? [];
    const sorted = sortPersistence.sortRows(data, sort);
    return Promise.resolve({ data: sorted, success: true, total: sorted.length });
  }, [expandableGroup?.data, sortPersistence.sortRows]);

  const baseRequest = expandableGroup ? groupedTableRequest : cachedTableRequest;
  const effectiveRequest = React.useCallback(
    async (params?: { current?: number; pageSize?: number }, sort?: Record<string, "ascend" | "descend">) => {
      const generation = ++requestGenerationRef.current;
      const queryKey = JSON.stringify({
        requestVersion,
        current: params?.current ?? 1,
        pageSize: params?.pageSize ?? pageSize,
        sort: sort ?? null,
      });
      try {
        const res = await baseRequest(params, sort);
        if (generation !== requestGenerationRef.current) {
          return { data: tableDataRef.current, success: true, total: tableTotalRef.current };
        }
        if (!res.success) throw new Error(text("List data could not be loaded."));
        setRequestError(null);
        if (!expandableGroup && res.success && Array.isArray(res.data)) {
          const visibleRows = dataMode === "server-query"
            ? (res.data as T[])
            : (sortPersistence.sortRows(res.data as T[], sort) as T[]);
          tableDataRef.current = visibleRows;
          tableTotalRef.current = Number(res.total ?? visibleRows.length);
          setTableTotal(tableTotalRef.current);
          tableSnapshotsRef.current.set(queryKey, { data: visibleRows, total: tableTotalRef.current });
          setTableData(visibleRows);
          onVisibleRowsChange?.(visibleRows);
          return { ...res, data: visibleRows };
        }
        if (!expandableGroup) onVisibleRowsChange?.([]);
        return res;
      } catch (error: unknown) {
        if (generation !== requestGenerationRef.current) {
          return { data: tableDataRef.current, success: true, total: tableTotalRef.current };
        }
        const msg = requestErrorMessage(error, text("List data could not be loaded."));
        setRequestError(msg);
        const snapshot = tableSnapshotsRef.current.get(queryKey);
        const fallbackRows = snapshot?.data ?? [];
        const fallbackTotal = snapshot?.total ?? 0;
        tableDataRef.current = fallbackRows;
        tableTotalRef.current = fallbackTotal;
        setTableTotal(fallbackTotal);
        setTableData(fallbackRows);
        if (!expandableGroup) onVisibleRowsChange?.(fallbackRows);
        return { data: fallbackRows, success: true, total: fallbackTotal, requestError: msg };
      }
    },
    [baseRequest, dataMode, expandableGroup, onVisibleRowsChange, pageSize, sortPersistence.sortRows, text]
  );
  React.useEffect(() => {
    if (!expandableGroup) return;
    void tableActionRef.current?.reload?.(false);
  }, [expandableGroup?.data, tableActionRef]);
  const effectiveColumns = expandableGroup ? expandableSummarySortableColumns : columns;
  const effectiveRowKey = expandableGroup ? (record: { key?: unknown }) => String(record?.key ?? "") : rowKeyFn;
  const handleTableChange = React.useCallback(
    (
      pagination: unknown,
      filters: unknown,
      sorter: AntdSorterInput | AntdSorterInput[] | null | undefined,
      extra: unknown
    ) => {
      // antd liefert das Paginierungsobjekt lose typisiert; gebraucht wird genau ein Feld.
      const nextPageSize = Number((pagination as { pageSize?: unknown } | null | undefined)?.pageSize);
      if (Number.isFinite(nextPageSize) && nextPageSize > 0) {
        setPageSize(nextPageSize);
        saveSettingsListPageSize(runtime.accountId, storageKey, nextPageSize);
      }
      sortPersistence.onTableChange(pagination, filters, sorter as Parameters<typeof sortPersistence.onTableChange>[2]);
      onChange?.(pagination, filters, sorter, extra);
    },
    [runtime.accountId, onChange, sortPersistence.onTableChange, storageKey]
  );

  // --- Kartenliste für schmale Viewports oder bei starkem Überlauf ---
  // Laeuft ueber react-query statt ueber einen eigenen Lader: der frühere Aufbau hatte einen
  // Generationszaehler als Wettlaufschutz und eine Map als Zwischenspeicher — beides Nachbauten
  // dessen, was der Query-Cache mitbringt.
  const requestSortForMode = React.useCallback(
    () => dataMode === "server-query" ? sortPersistence.getRequestSort() : undefined,
    [dataMode, sortPersistence.getRequestSort]
  );
  const cardSortForQuery = requestSortForMode();
  const cardQuery = useQuery({
    queryKey: buildListQueryKey(1, pageSize, cardSortForQuery),
    queryFn: () => tableRequest({ current: 1, pageSize }, cardSortForQuery),
    enabled: useCardViewEffective && !useRowsPresentation,
    // Der zuletzt erfolgreiche Stand bleibt sichtbar, waehrend nachgeladen wird — das ersetzt den
    // frueheren Rueckfall auf die Momentaufnahme im Fehlerfall.
    placeholderData: (previous: { data: T[]; success: boolean; total: number } | undefined) => previous
  }, queryClient);

  // --- Darstellung "rows": eine Abfrage für Raster und Karten, mit Seitenwechsel ---
  // server-query: Seite und Sortierung gehen an den Endpunkt. complete-scope: einmal laden, lokal
  // sortieren und blättern (wie Ant bei vollständiger Datenmenge).
  const rowsServerQuery = dataMode === "server-query";
  const [rowsPage, setRowsPage] = React.useState(1);
  const [rowsPageRequestVersion, setRowsPageRequestVersion] = React.useState(requestVersion);
  if (rowsPageRequestVersion !== requestVersion) {
    setRowsPageRequestVersion(requestVersion);
    setRowsPage(1);
  }
  const rowsRequestSort = rowsServerQuery ? requestSortForMode() : undefined;
  const rowsQuery = useQuery({
    queryKey: ["settings-list-rows", storageKey, ...(requestScopeKey === undefined ? [] : [requestScopeKey]), requestVersion, rowsServerQuery ? rowsPage : 1, pageSize, rowsRequestSort ?? null] as const,
    // Ohne Gruppierung (rows ist nur für flache Listen aktiv) liefert effectiveRequest Zeilen vom Typ T.
    queryFn: async () => (await effectiveRequest({ current: rowsServerQuery ? rowsPage : 1, pageSize }, rowsRequestSort)) as SettingsListRequestResult<T>,
    enabled: useRowsPresentation && !expandableGroup && !dataSourceMode,
    placeholderData: (previous: { data: T[]; success: boolean; total: number } | undefined) => previous
  }, queryClient);
  // Gruppierte Listen liefern ihre Gruppen selbst (`expandableGroup.data`) und werden lokal sortiert;
  // ebenso der Datenmodus „dataSource“ (bei gesteuertem Blättern nur die übergebene Seite).
  const rowsLoaded = expandableGroup
    ? (expandableGroup.data as unknown as T[])
    : dataSourceMode ? (dataSource as T[]) : rowsQuery.data?.data;
  const rowsLocalOnly = Boolean(expandableGroup) || (dataSourceMode ? !controlledPaging : !rowsServerQuery);
  const rowsSortLocally = rowsLocalOnly || (controlledPaging && !callerSortsOnServer);
  // Rückkanal der Sortierung an den Aufrufer (externes System: dessen Serverabfrage).
  const callerRequestSort = callerSortsOnServer ? sortPersistence.getRequestSort() : undefined;
  const callerRequestSortKey = JSON.stringify(callerRequestSort ?? null);
  const onRequestSortChangeRef = React.useRef(onRequestSortChange);
  React.useEffect(() => {
    onRequestSortChangeRef.current = onRequestSortChange;
  });
  React.useEffect(() => {
    if (!callerSortsOnServer) return;
    onRequestSortChangeRef.current?.(JSON.parse(callerRequestSortKey) ?? undefined);
  }, [callerSortsOnServer, callerRequestSortKey]);
  // `effectiveColumns` trägt die Systemsortierung (über makeColumnsSortable); ändert sie sich, wird neu sortiert.
  const rowsSorted = React.useMemo(() => {
    const loaded = rowsLoaded ?? [];
    return rowsSortLocally ? (sortPersistence.sortRows(loaded) as T[]) : loaded;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- effectiveColumns ist der Auslöser, nicht ein gelesener Wert.
  }, [rowsLoaded, rowsSortLocally, sortPersistence.sortRows, effectiveColumns]);
  const rowsPageSize = paginationProp === false
    ? Math.max(1, rowsSorted.length)
    : controlledPaging ? paginationOptions?.pageSize ?? pageSize : pageSize;
  const rowsTotal = controlledPaging
    ? Number(paginationOptions?.total)
    : rowsLocalOnly ? rowsSorted.length : Number(rowsQuery.data?.total ?? rowsSorted.length);
  const rowsCurrentPage = controlledPaging
    ? Number(paginationOptions?.current)
    : Math.min(rowsPage, Math.max(1, Math.ceil(rowsTotal / rowsPageSize)));
  const rowsVisible = rowsLocalOnly
    ? rowsSorted.slice((rowsCurrentPage - 1) * rowsPageSize, rowsCurrentPage * rowsPageSize)
    : rowsSorted;
  const summaryRow = React.useMemo(() => summary?.(rowsSorted), [rowsSorted, summary]);

  React.useEffect(() => {
    if (!useCardViewEffective || useRowsPresentation) return;
    const rows = cardQuery.data?.data;
    if (Array.isArray(rows)) onVisibleRowsChange?.(rows);
  }, [useCardViewEffective, cardQuery.data, onVisibleRowsChange]);

  React.useLayoutEffect(() => {
    if (!externalActionRef) return;
    // Ohne das Ungueltigmachen liefert der Cache beim Nachladen denselben Eintrag zurueck — die Liste saehe
    // dann trotz reload() unveraendert aus.
    const refreshRows = (): Promise<void> => {
      const pending = Promise.all([
        refreshQueriesAfterWrite(queryClient, { queryKey: ["settings-list", storageKey] }),
        refreshQueriesAfterWrite(queryClient, { queryKey: ["settings-list-rows", storageKey] }),
        refreshQueriesAfterWrite(queryClient, { queryKey: ["settings-list-scope-widen", storageKey] }),
      ]).then(() => undefined);
      // Background reloads expose their error in the list; awaited reloads still receive the same rejection.
      void pending.catch(() => undefined);
      return pending;
    };
    const revalidateCurrentQuery = refreshRows;
    const revalidateFromFirstPage = () => {
      setRowsPage(1);
      return refreshRows();
    };
    externalActionRef.current = {
      reload: (resetPageIndex?: boolean) => resetPageIndex ? revalidateFromFirstPage() : revalidateCurrentQuery(),
      reloadAndRest: () => {
        setRowsPage(1);
        rowSelectionProp?.onChange?.([], []);
        return refreshRows();
      },
      clearSelected: () => rowSelectionProp?.onChange?.([], []),
      revalidateCurrentQuery,
      revalidateFromFirstPage,
    } satisfies SettingsListActionRef;
  }, [externalActionRef, rowSelectionProp, storageKey, queryClient]);

  const cardBodyStyle = cardPadding === "spacious" ? cardDefaults.listCardBodyStyle : cardDefaults.listCardBodyStyleCompact;
  const listPageClassName = embedded ? undefined : "settings-list-page";
  const listPageWrapperStyle: React.CSSProperties = embedded
    ? { width: "100%", minWidth: 0 }
    : { width: "100%", minWidth: 0, flex: "0 0 auto", display: "flex", flexDirection: "column", minHeight: 0 };
  // Im Render abgeleitet statt im Effekt in den Zustand geschrieben: der Fehler der Kartenliste
  // steht bereits im Query-Ergebnis, ein zweiter Zustand daneben wuerde nur nachhinken.
  const cardRequestError = !useCardViewEffective
    ? null
    : cardQuery.error
      ? requestErrorMessage(cardQuery.error, text("List data could not be loaded."))
      : cardQuery.data && !cardQuery.data.success
        ? text("List data could not be loaded.")
        : null;
  const effectiveRequestError = cardRequestError ?? requestError;

  const requestErrorAlert = effectiveRequestError ? (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: spacing.lg }}
      message={text("List data could not be loaded.")}
      description={effectiveRequestError === text("List data could not be loaded.") ? undefined : effectiveRequestError}
    />
  ) : null;

  const loadedHitCount = expandableGroup
    ? (expandableGroup.loading ? null : expandableGroup.data.length)
    : useRowsPresentation
      ? (dataSourceMode ? (dataSourceLoading ? null : rowsTotal) : rowsQuery.isFetched ? rowsTotal : null)
    : useCardViewEffective
      ? (cardQuery.isFetched ? Number(cardQuery.data?.total ?? cardQuery.data?.data.length ?? 0) : null)
      : tableTotal != null
        ? tableTotal
        : tableData != null
          ? tableData.length
          : null;
  const scopeWidenCandidate = scopeWidenHint && !effectiveRequestError
    ? contextScopeWidenMode({
        loaded: runtime.scope?.loaded ?? false,
        allowedCount: runtime.scope?.allowedCount ?? 0,
        isAll: runtime.scope?.isAll ?? true,
        hitCount: loadedHitCount
      })
    : "none";
  // Die Vergleichsanfrage darf weder den angezeigten Bestand noch Formularzustand verändern.
  // request-Aufrufer mit Opt-in unterstützen scope="all"; dataSource liefert einen eigenen Zähler.
  const scopeWidenQuery = useQuery({
    queryKey: ["settings-list-scope-widen", storageKey, runtime.accountId, requestScopeKey ?? null,
      runtime.scope?.key ?? null, requestVersion, loadedHitCount,
      dataSourceMode ? dataSourceVersion : rowsQuery.dataUpdatedAt, cardQuery.dataUpdatedAt],
    queryFn: async () => {
      if (scopeWidenRequest) return scopeWidenRequest();
      if (!request || request.length < 1) return null;
      const result = await request({ current: 1, pageSize: 1, scope: "all" });
      if (!result.success) throw new Error("Scope comparison failed");
      return result.total;
    },
    enabled: scopeWidenCandidate !== "none" && Boolean(scopeWidenRequest || (request && request.length >= 1)),
    retry: false,
  }, queryClient);
  const scopeWiden = !scopeWidenQuery.isError && loadedHitCount != null
    && typeof scopeWidenQuery.data === "number" && Number.isFinite(scopeWidenQuery.data)
    && scopeWidenQuery.data > loadedHitCount ? scopeWidenCandidate : "none";
  const scopeWidenHintNode = (scopeWiden === "empty" || scopeWiden === "short") && loadedHitCount != null
    ? runtime.scope?.renderHint(loadedHitCount) ?? null
    : null;
  const listEmptyText = scopeWiden === "empty" && scopeWidenHintNode
    ? scopeWidenHintNode
    : emptyText;
  const scopeWidenAfterList = scopeWiden === "short" && scopeWidenHintNode
    ? <div style={{ marginTop: spacing.sm }}>{scopeWidenHintNode}</div>
    : null;

  // Darstellung: Raster bzw. gestapelte Karten (gemeinsame Zeilenliste, ohne ProTable).
  const currentSorterInput = (): AntdSorterInput | null => {
    const entry = Object.entries(sortPersistence.getRequestSort() ?? {})[0];
    return entry ? { field: entry[0], columnKey: entry[0], order: entry[1] } : null;
  };
  const handleRowsPageChange = (nextPage: number, nextPageSize: number) => {
    if (controlledPaging) {
      paginationOptions?.onChange?.(nextPage, nextPageSize);
      tableWrapperRef.current?.scrollIntoView?.({ block: "nearest" });
      return;
    }
    const sizeChanged = nextPageSize !== pageSize;
    if (sizeChanged) {
      setPageSize(nextPageSize);
      saveSettingsListPageSize(runtime.accountId, storageKey, nextPageSize);
    }
    setRowsPage(sizeChanged ? 1 : nextPage);
    onChange?.({ current: sizeChanged ? 1 : nextPage, pageSize: nextPageSize }, {}, currentSorterInput(), { action: "paginate" });
    tableWrapperRef.current?.scrollIntoView?.({ block: "nearest" });
  };
  const handleRowsSortChange = (column: ListColumn<T>, order: AntdSorterInput["order"]) => {
    const field = settingsListDataIndexPath(column.dataIndex) || String(column.key ?? "");
    setRowsPage(1);
    handleTableChange({ current: 1, pageSize }, {}, { field, columnKey: column.key, order }, { action: "sort" });
  };
  // Gruppen: Auswahl ganzer Gruppen, Detailzeilen als eingebettete Zeilenliste beim Aufklappen.
  const groupSelection = expandableGroup?.rowSelection;
  const toggleGroupSelection = (record: T) => {
    if (!expandableGroup || !groupSelection?.onChange) return;
    const group = record as unknown as SettingsListGroupRow<T>;
    if (groupSelection.getCheckboxProps?.(group)?.disabled) return;
    const groupKey = String(group.key);
    const selected = groupSelection.selectedRowKeys ?? [];
    const isSelected = selected.some((key) => String(key) === groupKey);
    const nextKeys = isSelected ? selected.filter((key) => String(key) !== groupKey) : [...selected, group.key];
    const nextKeySet = new Set(nextKeys.map(String));
    groupSelection.onChange(nextKeys, expandableGroup.data.filter((row) => nextKeySet.has(String(row.key))));
  };
  const renderGroupDetails = (record: T): React.ReactNode => {
    if (!expandableGroup) return null;
    const group = record as unknown as SettingsListGroupRow<T>;
    const detailRows = group.detailRows ?? [];
    const childKeys = expandableGroup.getChildColumnKeys?.(
      group,
      expandableSummaryColumnsWithSettings.map((column) => String(column.columnEditorKey ?? column.key ?? column.dataIndex))
    );
    const childColumns = childKeys
      ? expandableChildColumns.filter((column) => childKeys.includes(String(column.key ?? column.dataIndex)))
      : expandableChildColumns;
    return (
      <SettingsListRows<T>
        className="slr--nested"
        rowDrag={rowDrag}
        columns={childColumns as ListColumn<T>[]}
        rows={detailRows}
        total={detailRows.length}
        page={1}
        pageSize={Math.max(1, detailRows.length)}
        loading={false}
        stacked={rowsStacked}
        rowKey={rowKeyFn}
        onPageChange={() => undefined}
        onSortChange={() => undefined}
        hidePager
        sortable={false}
        expandedRowRender={expandableGroup.childExpandedRowRender ?? undefined}
        expandedRowKeys={expandableGroup.childExpandedRowKeys}
        formatValue={runtime.formatValue}
        text={text}
      />
    );
  };
  const renderStackedActions = (record: T, index: number): React.ReactNode => {
    if (expandableGroup) {
      const optionColumns = (effectiveColumns as ListColumn<T>[]).filter((column) => column.valueType === VALUE_TYPE_OPTION && typeof column.render === "function");
      const groupItems = optionColumns.flatMap((column) => {
        const rendered = (column.render as (value: unknown, record: T, index: number) => React.ReactNode)(undefined, record, index);
        return Array.isArray(rendered) ? rendered : [rendered];
      });
      return groupItems.some((item) => item != null)
        ? <Flex wrap gap="small">{groupItems.map((item, itemIndex) => <React.Fragment key={itemIndex}>{item}</React.Fragment>)}</Flex>
        : null;
    }
    let items: React.ReactNode[] = [];
    if (actionSlots?.length) {
      items = buildActionItemsFromSlots(record, false).filter((item): item is React.ReactNode => item != null);
    } else if (typeof actionsColumn?.render === "function") {
      const value = getSettingsListValueByPath(record, settingsListDataIndexPath(actionsColumn.dataIndex));
      const content = (actionsColumn.render as (value: unknown, record: T, index: number, action?: unknown) => React.ReactNode)(value, record, index, { cardView: true });
      items = (Array.isArray(content) ? content : [content]).filter((item) => item != null);
    }
    const normalized = normalizeSettingsListActionItems(items);
    if (normalized.length === 0) return null;
    return (
      <Flex wrap gap="small">
        {normalized.map((item, itemIndex) => <React.Fragment key={itemIndex}>{item}</React.Fragment>)}
      </Flex>
    );
  };
  const rowsThemeStyle = {
    "--slr-d-surface": token.colorBgContainer,
    "--slr-d-surface-hover": token.colorFillAlter,
    "--slr-d-line": token.colorBorderSecondary,
    "--slr-d-line-hover": token.colorBorder,
    "--slr-d-text": token.colorText,
    "--slr-d-text-2": token.colorTextSecondary,
    "--slr-d-text-3": token.colorTextTertiary,
    "--slr-d-accent": token.colorPrimary,
    "--slr-d-accent-soft": token.colorPrimaryBg,
    "--slr-d-radius": `${token.borderRadiusLG}px`,
  } as React.CSSProperties;
  const rowsSummaryKnown = expandableGroup ? !expandableGroup.loading : dataSourceMode ? !dataSourceLoading : Boolean(rowsQuery.data) && !rowsQuery.isPlaceholderData && (rowsQuery.isFetchedAfterMount || !rowsQuery.isFetching);
  return (
    <div ref={tableWrapperRef} className={listPageClassName} style={listPageWrapperStyle}>
      {titleExtra ? <div style={{ marginBottom: layoutDefaults.pageHeaderMarginBottom }}>{titleExtra}</div> : null}
      <SettingsListOuterFrame
        embedded={embedded}
        cardTitle={sectionTitle}
        cardBodyStyle={cardBodyStyle}
      >
        <SettingsListCardHeader headerTitle={embedded ? sectionTitle ?? headerTitle : headerTitle} headerExtra={headerExtra} inset={!embedded} />
        {beforeTable != null && <div className="settings-list-before-table" style={{ marginBottom: cardDefaults.listCardHeaderMarginBottom }}>{beforeTable}</div>}
        <SettingsListToolbarRow
          toolbarExtra={toolbarExtra}
          toolbarRightExtra={toolbarRightExtra}
          toolbarButtons={rowsStacked ? cardToolbarButtons : toolbarButtons}
          toolbarRightAlign={toolbarRightAlign}
          marginBottom={cardDefaults.listCardHeaderMarginBottom}
        />
        {requestErrorAlert}
        <div className="settings-list-rows-wrap" style={{ width: "100%" }}>
          <SettingsListRows<T>
            className={tableClassName}
            style={rowsThemeStyle}
            columns={effectiveColumns as ListColumn<T>[]}
            rows={rowsVisible}
            total={rowsTotal}
            page={rowsCurrentPage}
            pageSize={rowsPageSize}
            hidePager={paginationProp === false}
            pageSizeOptions={paginationOptions?.pageSizeOptions}
            summaryRow={summaryRow}
            summaryKnown={rowsSummaryKnown}
            hideFooter
            // Abblenden nur beim Erstaufbau und beim Blättern/Sortieren (Platzhalter = vorige Seite),
            // nicht bei Hintergrund-Neuladen nach Fokus oder Speichern (Lade-Standard). Erstaufbau heißt
            // auch: Cache-Stand aus einem früheren Besuch der Seite — der Schlüssel beginnt nach jedem
            // Einhängen wieder bei derselben `requestVersion`, der alte Stand galt sonst als fertig.
            loading={expandableGroup
              ? Boolean(expandableGroup.loading)
              : dataSourceMode
                ? dataSourceLoading
                : rowsQuery.isFetching && (rowsQuery.isPlaceholderData || !rowsQuery.data || !rowsQuery.isFetchedAfterMount)}
            stacked={rowsStacked}
            onFitChange={setRowsFit}
            actionProbe={actionProbe}
            rowKey={effectiveRowKey as (record: T) => string}
            onPageChange={handleRowsPageChange}
            onSortChange={handleRowsSortChange}
            rowSelection={(expandableGroup ? groupSelection : rowSelectionProp) as SettingsListPageProps<T>["rowSelection"]}
            onRowClick={expandableGroup ? undefined : effectiveOnRowClick}
            onRowClickForSelection={expandableGroup ? (groupSelection?.selectOnRowClick ? toggleGroupSelection : undefined) : onRowClickForSelection}
            expandable={expandableGroup ? { render: renderGroupDetails, rowExpandable: (record) => ((record as unknown as SettingsListGroupRow<T>).detailRows?.length ?? 0) > 0 } : expandableProp}
            onActiveRowKeyChange={onActiveRowKeyChange}
            activeRowKey={activeRowKey}
            keyboardNavigation={!embedded}
            rowClassName={buildRowClassName}
            rowReorder={rowReorder}
            rowDrag={rowDrag}
            expandedRowRender={expandedRowRender}
            expandedRowKeys={expandedRowKeys}
            emptyText={listEmptyText}
            paginationSummary={paginationSummary}
            showTotal={paginationOptions?.showTotal}
            renderStackedActions={renderStackedActions}
            formatValue={runtime.formatValue}
            text={text}
          />
          {scopeWidenAfterList}
        </div>
        {afterTable != null && <div style={{ marginTop: cardDefaults.listCardHeaderMarginBottom }}>{afterTable}</div>}
      </SettingsListOuterFrame>
      <SettingsListFooter total={rowsTotal} page={rowsCurrentPage} pageSize={rowsPageSize}
        pageSizeOptions={paginationOptions?.pageSizeOptions} hidePager={paginationProp === false}
        summaryKnown={rowsSummaryKnown} paginationSummary={paginationSummary} showTotal={paginationOptions?.showTotal}
        onPageChange={handleRowsPageChange} text={text} style={rowsThemeStyle} />
      {columnEditor.modal}
    </div>
  );
}

export type { ColumnDefinition };
